import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { toast, Toaster } from 'react-hot-toast';
import {
  ChevronLeft,
  Download,
  Copy,
  Check,
  Loader,
  RefreshCw,
  Send,
  Upload,
  X,
  AlertCircle,
  Eye,
  Edit,
  Save,
  MessageSquare,
  FileText,
  Users
} from 'react-feather';
import {
  Globe,
  Languages,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  Zap,
  Building,
  Shield,
  Briefcase,
  Newspaper,
  Search,
  ArrowRight,
  PlayCircle,
  PauseCircle,
  StopCircle,
  RotateCcw,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  Link,
  Tag,
  Hash,
  Type,
  Bold,
  Italic,
  List,
  Quote,
  Code,
  Image,
  Table,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Underline,
  Strikethrough,
  Database,
  BarChart3,
  Target,
  TrendingUp,
  DollarSign,
  Palette,
  Loader2
} from 'lucide-react';
import MDEditor, { commands } from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import { authenticatedFetch } from '../utils/auth';
import { quickAuthCheck, getFreshUnifiedStatus } from '../services/unifiedAuthService';
import logger from '../utils/logger';
import { API_BASE_URL, DEV_MODE, BYPASS_AUTH, getApiBaseUrl } from '../utils/config';
import { useLanguage } from '../hooks/useLanguage';
import { useAdmin } from '../context/AdminContext';

import DocumentManager from './DocumentManager';
import SectionSelectionModal from './SectionSelectionModal';
import { RESEARCH_STATUS } from '../constants/researchStatus';
// Import the team permissions hook
import { useTeamPermissions } from '../hooks/useTeamPermissions';
// Import highlighting utilities
import { createAllHighlightCommands } from '../utils/highlightUtils.jsx';
// Import WYSIWYG editor
import WYSIWYGEditor from './WYSIWYGEditor';
// Import report section markdown styles
import './ResearchDashboard.css';
// Import markdown sanitizer (auto-fixes AI-generated markdown issues)
import { sanitizeMarkdown, describeSanitizationDiff } from '../utils/markdownSanitizer';

const apiBaseUrl = getApiBaseUrl();
// console.log("API Base URL:", apiBaseUrl);


// Helper function that takes dashboardId as a parameter



const getDashboardKey = (dashboardId, key) => {
  if (!dashboardId) return null;
  return `dashboard_${dashboardId}_${key}`;
};

// Research always generates bilingual EN + JP reports for all 13 agents.
const REPORT_GENERATION_LANGUAGE = 'both';
const AGENT_API_LANGUAGE = 'Both';

const getUiViewLanguage = () => {
  const lng = localStorage.getItem('i18nextLng') || 'ja';
  return lng.startsWith('en') ? 'en' : 'ja';
};

// Add these constants at the top of the file, outside the component
const REQUIRED_SECTIONS = [
  'basic_information',
  'business_structure',
  'vision_analysis',
  'management_strategy',
  'management_message',
  'crisis_management',
  'dx_analysis',
  'financial_analysis',
  'competitive_landscape',
  'regulatory_environment',
  'salesforce_analysis',
  'latest_news',
  'account_research'
];

// Polling interval for research progress (in milliseconds)
const POLLING_INTERVAL = 5000; // 5 seconds - frequent enough to show new sections promptly

// Create highlighting commands using utility functions
const highlightCommands = createAllHighlightCommands();

const ResearchDashboard = () => {
  // Debug log for missing function
  console.log('DEBUG: getFreshUnifiedStatus type:', typeof getFreshUnifiedStatus);

  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { keycloak, initialized } = useKeycloak();
  const { isOwner, hasWriteAccess, hasReadAccess, refreshPermissions } = useTeamPermissions();
  const { integration } = useAdmin();
  // Integration readiness — informational only; the warning is shown only on DashboardPage
  const isIntegrationReady = integration
    ? integration.is_configured && Object.values(integration.agent_ids_configured ?? {}).some(Boolean)
    : false;

  // Dashboard state
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);

  // Company and UI state
  const [company, setCompany] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [showSubsidiaryChoice, setShowSubsidiaryChoice] = useState(false);
  const [manualSubsidiary, setManualSubsidiary] = useState('');
  const [viewMode, setViewMode] = useState('view');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDocumentSidebar, setShowDocumentSidebar] = useState(false);
  const [researchInProgress, setResearchInProgress] = useState(false);

  // Processing and Loading States
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isPromptLoading, setIsPromptLoading] = useState(false);
  const [isFetchingSections, setIsFetchingSections] = useState(false);
  const [isProcessComplete, setIsProcessComplete] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [processingTimeLeft, setProcessingTimeLeft] = useState(0);
  const [pollingController, setPollingController] = useState(null);
  const [pollingStartTime, setPollingStartTime] = useState(null);
  const [researchStatus, setResearchStatus] = useState(RESEARCH_STATUS.NOT_STARTED);
  const [pendingTaskId, setPendingTaskId] = useState(null);
  const [pendingTimestamp, setPendingTimestamp] = useState(null);

  // Add these refs for interval management
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Add flag to prevent duplicate auto-start attempts
  const englishAutoStartAttempted = useRef(false);

  // File and Document States
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [fileCount, setFileCount] = useState(0);
  const [dashboardDataExists, setDashboardDataExists] = useState(false);
  const [showStartButton, setShowStartButton] = useState(true);
  const [isMarkdown, setIsMarkdown] = useState(false);
  // Report and Sections States
  const [report, setReport] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  const [sections, setSections] = useState([]);
  const [reportSections, setReportSections] = useState({});
  // displayLanguage drives what language content is shown in the right panel (view only).
  const [displayLanguage, setDisplayLanguage] = useState(() => (
    getUiViewLanguage() === 'en' ? 'en' : 'jp'
  ));

  const [activeSection, setActiveSection] = useState(null);
  // Keep latest selection for polling closures; stop auto-jump after user picks a tab
  const activeSectionRef = useRef(null);
  const hasUserSelectedSectionRef = useRef(false);

  const applyActiveSection = useCallback((sectionKey, { userInitiated = false } = {}) => {
    if (userInitiated) {
      hasUserSelectedSectionRef.current = true;
    }
    activeSectionRef.current = sectionKey;
    setActiveSection(sectionKey);
    if (sectionKey) {
      localStorage.setItem(`dashboard_${dashboardId}_activeSection`, sectionKey);
    }
  }, [dashboardId]);

  const shouldAutoSelectSection = useCallback(() => (
    !activeSectionRef.current && !hasUserSelectedSectionRef.current
  ), []);

  const clearActiveSectionSelection = useCallback(() => {
    hasUserSelectedSectionRef.current = false;
    activeSectionRef.current = null;
    setActiveSection(null);
  }, []);

  // Chat and Prompt States
  const [chatMessages, setChatMessages] = useState([]);
  const [promptText, setPromptText] = useState('');

  // Translation States — controls which section endpoint the UI reads (view only).
  const [platformLanguage, setPlatformLanguage] = useState(() => getUiViewLanguage());

  // Report generation is always bilingual; EN/JP UI toggle does not change agent inputs.
  const [reportLanguage] = useState(REPORT_GENERATION_LANGUAGE);

  // UI language — global singleton shared across all pages
  const [uiLanguage, setUiLanguage] = useLanguage();

  useEffect(() => {
    localStorage.setItem('reportLanguage', REPORT_GENERATION_LANGUAGE);
  }, []);

  // UI language toggle controls viewing only — not what agents generate.
  useEffect(() => {
    if (uiLanguage === 'en') {
      setDisplayLanguage('en');
      setPlatformLanguage('en');
      if (dashboardId) localStorage.setItem(`dashboard_${dashboardId}_platformLanguage`, 'en');
    } else if (uiLanguage === 'ja') {
      setDisplayLanguage('jp');
      setPlatformLanguage('ja');
      if (dashboardId) localStorage.setItem(`dashboard_${dashboardId}_platformLanguage`, 'ja');
    }
  }, [uiLanguage, dashboardId]);

  const handleUiLanguageChange = (lang) => setUiLanguage(lang);

  const [translatedSections, setTranslatedSections] = useState({});
  const [sectionsTranslating, setSectionsTranslating] = useState({});
  const [translationCache, setTranslationCache] = useState({});
  const [isGlobalTranslating, setIsGlobalTranslating] = useState(false);
  const [isInterfaceTranslating, setIsInterfaceTranslating] = useState(false);
  const [isChatTranslating, setIsChatTranslating] = useState(false);

  // Section selection modal state
  const [showSectionSelectionModal, setShowSectionSelectionModal] = useState(false);
  const [interfaceTranslations, setInterfaceTranslations] = useState({});
  const [translatedChatMessages, setTranslatedChatMessages] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [hasTranslatedSections, setHasTranslatedSections] = useState(false);
  const [contentRefreshKey, setContentRefreshKey] = useState(0);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [title, setTitle] = useState('');
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const chatHasScrolledRef = useRef(false);
  const bilingualSubsidiaryRefreshRef = useRef(false);

  // Add this state to track if Japanese research is complete
  const [isJapaneseComplete, setIsJapaneseComplete] = useState(false);
  const [isEnglishComplete, setIsEnglishComplete] = useState(false);
  // Function to track sections currently being translated
  const hasJapaneseContent = (text) => {
    if (!text || typeof text !== 'string') return false;
    // Check for Japanese characters (Hiragana, Katakana, Kanji)
    return /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(text);
  };

  const isSectionCompleteForLang = useCallback((sectionKey, lang) => {
    if (lang === 'ja') {
      const jaContent = translatedSections[sectionKey]?.ja;
      return jaContent && jaContent !== '{}' && jaContent.trim() !== '' && jaContent !== 'undefined';
    } else {
      const enContent = reportSections[sectionKey];
      return enContent && enContent !== '{}' && enContent.trim() !== '' && enContent !== 'undefined';
    }
  }, [reportSections, translatedSections]);


  // Add these new state variables near the other state declarations
  const [stockTicker, setStockTicker] = useState('');
  const [industry, setIndustry] = useState('');


  // Helper function to get ordered section keys - placed at component level to ensure global accessibility
  const getOrderedSectionKeys = (sections) => {
    // Define the desired order of sections matching backend requirements
    const sectionOrder = REQUIRED_SECTIONS;

    // Map our frontend keys to backend keys (handles both shortened and full keys)
    const keyMapping = {
      // Shortened to full
      'basic': 'basic_information',
      'vision': 'vision_analysis',
      'management': 'management_strategy',
      'dx': 'dx_analysis',
      'financial': 'financial_analysis',
      'competitive': 'competitive_landscape',
      'salesforce': 'salesforce_analysis',
      'news': 'latest_news',
      'account_strategy': 'account_research',
      // Full to full (identity mapping)
      'basic_information': 'basic_information',
      'business_structure': 'business_structure',
      'vision_analysis': 'vision_analysis',
      'management_strategy': 'management_strategy',
      'management_message': 'management_message',
      'crisis_management': 'crisis_management',
      'dx_analysis': 'dx_analysis',
      'financial_analysis': 'financial_analysis',
      'competitive_landscape': 'competitive_landscape',
      'regulatory_environment': 'regulatory_environment',
      'salesforce_analysis': 'salesforce_analysis',
      'latest_news': 'latest_news',
      'account_research': 'account_research'
    };

    // Always return all required sections in order, regardless of what data exists.
    // This ensures all tabs are always visible (with loading/empty state as needed).
    return sectionOrder;
  };

  // Helper to ensure all REQUIRED_SECTIONS keys exist in a sections object.
  // Missing keys get an empty string value so tabs always appear.
  const normalizeReportSections = (sections) => {
    const normalized = {};
    REQUIRED_SECTIONS.forEach(key => {
      normalized[key] = sections[key] || '';
    });
    return normalized;
  };

  const buildJapaneseSectionMap = (sections = {}) => {
    const mapped = {};
    Object.entries(sections).forEach(([key, value]) => {
      mapped[key] = { ja: value || '' };
    });
    return mapped;
  };

  const ensureSectionKeys = (sections = {}) => {
    setReportSections(prev => {
      const next = { ...prev };
      REQUIRED_SECTIONS.forEach(sectionKey => {
        if (Object.prototype.hasOwnProperty.call(sections, sectionKey) && next[sectionKey] === undefined) {
          next[sectionKey] = '';
        }
      });
      return next;
    });
  };


  // Useffect to set state form localstorage

  useEffect(() => {
    setCompany(localStorage.getItem(`dashboard_${dashboardId}_company`) || '');

    // Bilingual generation is always on; persist for any legacy readers of this key.
    localStorage.setItem('reportLanguage', REPORT_GENERATION_LANGUAGE);
    localStorage.setItem(`dashboard_${dashboardId}_reportLanguage`, REPORT_GENERATION_LANGUAGE);

    // UI language is managed globally by useLanguage hook — no restore needed here

    setReport(localStorage.getItem(`dashboard_${dashboardId}_report`) || '');
    setChatMessages(JSON.parse(localStorage.getItem(`dashboard_${dashboardId}_messages`)) || []);
    setReportSections(normalizeReportSections(JSON.parse(localStorage.getItem(`dashboard_${dashboardId}_reportSections`)) || {}));
    const storedActiveSection = localStorage.getItem(`dashboard_${dashboardId}_activeSection`) || null;
    activeSectionRef.current = storedActiveSection;
    if (storedActiveSection) {
      hasUserSelectedSectionRef.current = true;
    }
    setActiveSection(storedActiveSection);
    setFileCount(parseInt(localStorage.getItem(`dashboard_${dashboardId}_fileCount`)) || 0);
    setReportGenerated(false);
    setSelectedCompany(localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`) || '');
    setProcessingStep(localStorage.getItem(`dashboard_${dashboardId}_processingStep`) || '');
    setProgressPercentage(parseInt(localStorage.getItem(`dashboard_${dashboardId}_progressPercentage`)) || 0);
    setIsProcessComplete(localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`) === 'true');
    setProcessingTimeLeft(parseInt(localStorage.getItem(`dashboard_${dashboardId}_processingTimeLeft`)) || 0);
    setManualSubsidiary(localStorage.getItem(`dashboard_${dashboardId}_manualSubsidiary`) || '');
    setCompanyName(localStorage.getItem(`dashboard_${dashboardId}_companyName`) || '');
    setPromptText(localStorage.getItem(`dashboard_${dashboardId}_promptText`) || '');
    setReportSections(normalizeReportSections(JSON.parse(localStorage.getItem(`dashboard_${dashboardId}_reportSections`)) || {}));
    try {
      const storedSubs = JSON.parse(localStorage.getItem(`dashboard_${dashboardId}_subsidiaries`) || '[]');
      setSubsidiaries(Array.isArray(storedSubs) ? storedSubs : []);
    } catch {
      setSubsidiaries([]);
    }
    setShowSubsidiaryChoice(localStorage.getItem(`dashboard_${dashboardId}_showSubsidiaryChoice`) === 'true');
    setTranslatedSections(JSON.parse(localStorage.getItem(`dashboard_${dashboardId}_translatedSections`) || '{}') || {});
    setTranslationCache(JSON.parse(localStorage.getItem(`dashboard_${dashboardId}_translationCache`)) || {});
    setIsGlobalTranslating(localStorage.getItem(`dashboard_${dashboardId}_isGlobalTranslating`) === 'true');
    setIsInterfaceTranslating(localStorage.getItem(`dashboard_${dashboardId}_isInterfaceTranslating`) === 'true');
    setIsChatTranslating(localStorage.getItem(`dashboard_${dashboardId}_isChatTranslating`) === 'true');
    setIsProcessing(localStorage.getItem(`dashboard_${dashboardId}_isProcessing`) === 'true');
    setIsCheckingStatus(localStorage.getItem(`dashboard_${dashboardId}_isCheckingStatus`) === 'true');
    setViewMode(localStorage.getItem(`dashboard_${dashboardId}_viewMode`) || 'view');
    setDashboardDataExists(localStorage.getItem(`dashboard_${dashboardId}_dashboardDataExists`) === 'true');
    setShowStartButton(localStorage.getItem(`dashboard_${dashboardId}_showStartButton`) === 'true');
    setIsSearching(localStorage.getItem(`dashboard_${dashboardId}_isSearching`) === 'true');
    setIsPromptLoading(localStorage.getItem(`dashboard_${dashboardId}_isPromptLoading`) === 'true');
    setIsLoading(localStorage.getItem(`dashboard_${dashboardId}_isLoading`) === 'true');
    setInterfaceTranslations(JSON.parse(localStorage.getItem(`dashboard_${dashboardId}_interfaceTranslations`)) || {});
    setTranslatedChatMessages(JSON.parse(localStorage.getItem(`dashboard_${dashboardId}_translatedChatMessages`)) || {});
    setIsJapaneseComplete(localStorage.getItem(`dashboard_${dashboardId}_is_japanese_complete`) === 'true');
    setResearchInProgress(localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true');
    const storedResearchStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);
    if (storedResearchStatus && Object.values(RESEARCH_STATUS).includes(storedResearchStatus)) {
      setResearchStatus(storedResearchStatus);
    }
  }, []);



  useEffect(() => {
    const initDashboard = async () => {
      // Prevent multiple initializations
      if (!dashboardId || !keycloak.token || isInitializedRef.current) {
        console.log('Skipping initialization:', {
          hasDashboardId: !!dashboardId,
          hasToken: !!keycloak.token,
          isInitialized: isInitializedRef.current
        });
        return;
      }

      console.log('Starting dashboard initialization');
      isInitializedRef.current = true;

      try {
        // Add authorization check at the beginning of initialization using unified service
        console.log('[initDashboard] Checking authorization before initializing dashboard');

        // Use fresh data for dashboard initialization
        console.log('DEBUG: Calling getFreshUnifiedStatus...');
        const authData = await getFreshUnifiedStatus({ includeIntegrationStatus: false });

        if (authData.is_authorized !== true) {
          // Check for Developer Bypass Mode - don't logout if bypassing
          const isDevBypass = BYPASS_AUTH || DEV_MODE;

          if (isDevBypass) {
            console.log('[initDashboard] Auth check failed but in DEV_BYPASS mode, continuing...');
          } else {
            console.log('[initDashboard] User is not authorized, redirecting to dashboards');

            // Update localStorage to reflect unauthorized status
            localStorage.setItem('isUserAuthorized', 'false');

            // Clear auth data
            localStorage.removeItem('isUserAuthorized');
            localStorage.removeItem('adminUsers');
            sessionStorage.removeItem('wasAuthorized');

            // Clear any other cached data
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('auth_') || key.startsWith('user_')) {
                localStorage.removeItem(key);
              }
            });

            // Redirect to dashboards with error message instead of logging out
            // This avoids the login loop while still protecting the content
            toast.error(t('common.unauthorized') || 'You are not authorized to view this dashboard. Please complete the setup or contact your administrator.');

            setTimeout(() => {
              navigate('/dashboards');
            }, 2000);

            return;
          }
        }

        console.log('[initDashboard] Authorization check passed, proceeding with initialization');

        // Check if dashboard is in processing mode
        const isProcessingActive = localStorage.getItem(`dashboard_${dashboardId}_isProcessing`) === 'true';
        const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';
        const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);

        // If research is in progress, check if already complete before resuming polling
        const pendingTaskIdFromStore = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
        if (isResearchInProgress && pendingTaskIdFromStore) {
          console.log('Research was in progress, checking if already complete before resuming polling');
          setPendingTaskId(pendingTaskIdFromStore);

          // Quick check: are all sections already in the DB?
          const researchLang = localStorage.getItem(`dashboard_${dashboardId}_initial_research_language`) || platformLanguage;
          const checkEndpoint = researchLang === 'ja'
            ? `/api/dashboards/${dashboardId}/sections/japanese`
            : `/api/dashboards/${dashboardId}/sections`;

          let sectionsAlreadyComplete = false;
          try {
            const checkResp = await authenticatedFetch(checkEndpoint, { method: 'GET' }).catch(() => ({ ok: false }));
            if (checkResp.ok) {
              const existingSections = await checkResp.json();
              const completedCount = REQUIRED_SECTIONS.filter(key =>
                existingSections[key] && existingSections[key] !== '{}' && existingSections[key].trim() !== ''
              ).length;
              sectionsAlreadyComplete = completedCount === REQUIRED_SECTIONS.length;
              console.log(`Pre-poll section check: ${completedCount}/${REQUIRED_SECTIONS.length} complete`);
            }
          } catch (e) {
            console.warn('Could not pre-check sections:', e);
          }

          if (sectionsAlreadyComplete) {
            // Research is already done — clear processing state without restarting polling
            console.log('All sections already complete, skipping polling restart');
            setIsProcessing(false);
            setResearchInProgress(false);
            setIsProcessComplete(true);
            setResearchStatus(RESEARCH_STATUS.COMPLETED);
            localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.COMPLETED);
            localStorage.setItem(`dashboard_${dashboardId}_researchInProgress`, 'false');
            localStorage.setItem(`dashboard_${dashboardId}_isProcessing`, 'false');
            localStorage.setItem(`dashboard_${dashboardId}_isProcessComplete`, 'true');
          } else {
            // Still in progress — resume polling + keep Cancel Research visible
            setIsProcessing(true);
            setResearchInProgress(true);
            setResearchStatus(RESEARCH_STATUS.IN_PROGRESS);
            setIsProcessComplete(false);
            localStorage.setItem(`dashboard_${dashboardId}_isProcessing`, 'true');
            localStorage.setItem(`dashboard_${dashboardId}_researchInProgress`, 'true');
            localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.IN_PROGRESS);
            if (!localStorage.getItem(`dashboard_${dashboardId}_processingStep`)) {
              setProcessingStep('researchDashboard.processingSteps.initializingResearch');
            }

            // Initialize section states to show loading for all sections
            const initialStates = {};
            REQUIRED_SECTIONS.forEach(section => {
              initialStates[section] = {
                content: '',
                isLoading: true,
                lastUpdated: null,
                hasData: false,
                status: 'loading',
                error: null,
                progressPercentage: 0
              };
            });
            setSectionStates(initialStates);

            // Initialize reportSections with empty content
            const initialReportSections = {};
            REQUIRED_SECTIONS.forEach(section => {
              initialReportSections[section] = '';
            });
            setReportSections(initialReportSections);

            startPollingWithControls(pendingTaskId);
          }
          setReportGenerated(true);
        } else if (isProcessingActive || pendingTaskId) {
          // Clear stale processing state if research is not in progress
          console.log('Clearing stale processing state');
          localStorage.removeItem(`dashboard_${dashboardId}_isProcessing`);
          localStorage.removeItem(`dashboard_${dashboardId}_researchInProgress`);
          localStorage.removeItem(`dashboard_${dashboardId}_pendingTaskId`);
        }

        // Fetch dashboard details once
        console.log('Fetching dashboard details');
        const dashboardResponse = await authenticatedFetch(
          `/api/dashboards/${dashboardId}`,
          { method: 'GET' }
        );

        if (!dashboardResponse.ok) {
          throw new Error(`Failed to get dashboard: ${dashboardResponse.statusText}`);
        }

        const dashboardData = await dashboardResponse.json();

        // Set dashboard data
        setDashboard(dashboardData);
        setDashboardLoaded(true);

        // Check if dashboard has company name and research data
        if (dashboardData && dashboardData.company_name) {
          setCompanyName(dashboardData.company_name);
          setSelectedCompany(dashboardData.company_name);
          localStorage.setItem(`dashboard_${dashboardId}_selectedCompany`, dashboardData.company_name);

          // Check content based on current platform language
          const endpoint = platformLanguage === 'ja'
            ? `/api/dashboards/${dashboardId}/sections/japanese`
            : `/api/dashboards/${dashboardId}/sections`;

          console.log(`Checking existing content for language: ${platformLanguage}`);

          const response = await authenticatedFetch(
            endpoint,
            { method: 'GET' }
          ).catch(() => ({ ok: false }));

          if (response.ok) {
            const sections = await response.json();
            const hasContent = Object.values(sections).some(
              content => content && content !== '{}' && content.trim() !== ''
            );

            if (hasContent) {
              console.log(`Found existing content for ${platformLanguage}`);
              const stillRunning =
                localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true' &&
                !!localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);

              if (platformLanguage === 'ja') {
                if (!stillRunning) {
                  setIsJapaneseComplete(true);
                  localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
                }
                setTranslatedSections(prev => ({ ...prev, ...buildJapaneseSectionMap(sections) }));
                
                // CRITICAL: Also initialize reportSections with keys so tabs know sections exist
                const initializedSections = {};
                REQUIRED_SECTIONS.forEach(sectionKey => {
                  initializedSections[sectionKey] = sections[sectionKey] ? '' : '';
                });
                setReportSections(initializedSections);
                
                ensureSectionKeys(sections);
              } else {
                if (!stillRunning) {
                  setIsEnglishComplete(true);
                  localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'true');
                }
                setReportSections(normalizeReportSections(sections));
              }
              setReportGenerated(true);
              // Do NOT kill processing UI / Cancel when research still running (partial sections ok)
              if (!stillRunning) {
                setIsProcessing(false);
              }

              // Enable the document button when existing content is loaded
              enableDocumentButton();

              // Select first section only if no section is currently active
              const orderedKeys = getOrderedSectionKeys(sections);
              if (orderedKeys.length > 0 && shouldAutoSelectSection()) {
                applyActiveSection(orderedKeys[0]);
              }
            } else {
              console.log(`No content found for ${platformLanguage}`);
              setShowStartButton(true);
            }
          } else {
            console.log(`Failed to fetch content for ${platformLanguage}`);
            setShowStartButton(true);
          }
        } else {
          console.log('No company name found, showing start button');
          setShowStartButton(true);
        }
      } catch (error) {
        console.error('Error during initialization:', error);
        setShowStartButton(true);
      } finally {
        setIsInitialLoad(false);
      }
    };

    // Only run initialization once
    if (!isInitializedRef.current) {
      console.log('Running initial dashboard setup');
      initDashboard();
    }

    // Cleanup function
    return () => {
      console.log('Cleaning up dashboard');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, [dashboardId, keycloak.token]); // Minimal dependencies

  useEffect(() => {
    // Don't start auto-refresh if research is already complete
    const isComplete = localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`) === 'true';
    const currentStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);

    // Skip auto-refresh entirely if research is completed
    if (isComplete || currentStatus === 'COMPLETED') {
      console.log('Research is complete, skipping auto-refresh setup');
      return;
    }

    const autoRefreshInterval = setInterval(() => {
      // Double-check completion status before each refresh
      const isStillComplete = localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`) === 'true';
      const currentRefreshStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);

      if (isStillComplete || currentRefreshStatus === 'COMPLETED') {
        console.log('Research completed during auto-refresh, stopping interval');
        clearInterval(autoRefreshInterval);
        return;
      }

      if (!isProcessing) {
        refreshData();
      }
    }, 60000); // Check every minute

    return () => {
      clearInterval(autoRefreshInterval);
    };
  }, [isProcessing, dashboardId]); // Added dashboardId to dependencies


  useEffect(() => {
    // Skip language-related actions if dashboard is in processing state
    const isProcessingActive = localStorage.getItem(`dashboard_${dashboardId}_isProcessing`) === 'true';
    const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';
    const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);

    // If dashboard is in active processing mode, only handle polling
    if (isProcessingActive || isResearchInProgress || pendingTaskId) {
      console.log('Dashboard is in active processing mode, only handling polling for language change');

      // Still allow polling to continue in the correct language
      if (pendingTaskId) {
        setIsProcessing(true);
        initializeSectionStates();
        startPollingWithControls(pendingTaskId);
        setReportGenerated(true);
      }
      return;
    }

    // Rest of the existing language change logic for non-processing dashboards
    if (platformLanguage === 'en') {
      // Existing English language code...
    }
    else if (platformLanguage === 'ja') {
      // Existing Japanese language code...
    }
  }, [platformLanguage]);



  // Function to add a message to the chat
  const addMessage = async (type, content, options = {}) => {
    const newMessage = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      // Never store null — empty string keeps bubble layout stable if i18nKey is missing
      content: content ?? '',
      timestamp: new Date().toISOString(),
      ...options
    };

    setChatMessages(prevMessages => {
      const updatedMessages = [...prevMessages, newMessage];
      localStorage.setItem(`dashboard_${dashboardId}_messages`, JSON.stringify(updatedMessages));
      return updatedMessages;
    });
  };

  // Keep chat pinned to latest (refresh / open report / new messages)
  const scrollChatToBottom = (behavior = 'auto') => {
    const container = chatContainerRef.current;
    if (container) {
      if (behavior === 'smooth') {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  };

  useEffect(() => {
    chatHasScrolledRef.current = false;
  }, [dashboardId]);

  // Upgrade legacy monolingual subsidiary lists → bilingual (one refresh)
  useEffect(() => {
    if (!showSubsidiaryChoice) {
      bilingualSubsidiaryRefreshRef.current = false;
      return;
    }
    if (!keycloak?.token || bilingualSubsidiaryRefreshRef.current) return;

    const missingBilingual = subsidiaries.some(
      (s) => typeof s === 'object' && s && (!s.name_en || !s.name_jp)
    );
    if (!missingBilingual || subsidiaries.length === 0) {
      bilingualSubsidiaryRefreshRef.current = true;
      return;
    }

    const lookupName =
      newCompanyName ||
      companyName ||
      localStorage.getItem(`dashboard_${dashboardId}_companyName`) ||
      (typeof subsidiaries[0] === 'string'
        ? subsidiaries[0]
        : subsidiaries[0]?.name_en || subsidiaries[0]?.name);

    if (!lookupName) return;

    let cancelled = false;
    bilingualSubsidiaryRefreshRef.current = true;

    (async () => {
      try {
        const { authenticatedFetch } = await import('../utils/auth');
        const res = await authenticatedFetch(
          'api/company',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              company_name: lookupName,
              language: 'English',
            }),
          },
          keycloak.token
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled || data.lookup_failed || !data.subsidiaries?.length) return;
        setSubsidiaries(data.subsidiaries);
        localStorage.setItem(
          `dashboard_${dashboardId}_subsidiaries`,
          JSON.stringify(data.subsidiaries)
        );
      } catch (e) {
        console.warn('Bilingual subsidiary refresh failed', e);
        bilingualSubsidiaryRefreshRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    showSubsidiaryChoice,
    subsidiaries,
    dashboardId,
    keycloak?.token,
    newCompanyName,
    companyName,
  ]);

  useEffect(() => {
    if (chatMessages.length === 0) return;
    const behavior = chatHasScrolledRef.current ? 'smooth' : 'auto';
    const raf = requestAnimationFrame(() => {
      scrollChatToBottom(behavior);
      // Second pass after layout (markdown / late UI) — keep pinned on open/refresh
      if (!chatHasScrolledRef.current) {
        setTimeout(() => scrollChatToBottom('auto'), 100);
      }
      chatHasScrolledRef.current = true;
    });
    return () => cancelAnimationFrame(raf);
  }, [chatMessages]);

  // Function to render chat messages
  const renderChatMessage = (message) => {
    const isAiMessage = message.type === 'ai' || message.is_user === false;

    const bilingualContent = uiLanguage === 'ja'
      ? (message.content_jp || message.content_en || message.content)
      : (message.content_en || message.content_jp || message.content);

    const displayContent = message.i18nKey
      ? t(message.i18nKey, message.i18nParams)
      : (bilingualContent ?? '');

    if (isAiMessage && message.isLoading) {
      return (
        <div className="flex items-center text-left">
          <div className="flex space-x-2 flex-shrink-0">
            <div className="h-2 w-2 bg-gray-500 rounded-full animate-pulse"></div>
            <div className="h-2 w-2 bg-gray-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="h-2 w-2 bg-gray-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
          <span className="ml-2 text-sm text-gray-500">{displayContent}</span>
        </div>
      );
    }

    if (typeof displayContent === 'string') {
      if (message.isMarkdown) {
        return (
          <div className="markdown-content text-left">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ node, ...props }) => <h1 className="report-h1" {...props} />,
                h2: ({ node, ...props }) => <h2 className="report-h2" {...props} />,
                h3: ({ node, ...props }) => <h3 className="report-h3" {...props} />,
                h4: ({ node, ...props }) => <h4 className="report-h4" {...props} />,
                p: ({ node, ...props }) => <p className="report-p" {...props} />,
                a: ({ node, href, ...props }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="report-link"
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => <ul className="report-ul" {...props} />,
                ol: ({ node, ...props }) => <ol className="report-ol" {...props} />,
                li: ({ node, ...props }) => <li className="report-li" {...props} />,
                code: ({ node, inline, ...props }) => (
                  inline
                    ? <code className="report-code-inline" {...props} />
                    : <code className="report-code-block" {...props} />
                ),
                strong: ({ node, ...props }) => <strong className="report-strong" {...props} />,
              }}
            >
              {displayContent}
            </ReactMarkdown>
          </div>
        );
      }
      return <div className="whitespace-pre-wrap text-left">{displayContent}</div>;
    }

    return <div className="text-left">{String(displayContent ?? '')}</div>;
  };

  // Universal helper to get text based on current language
  const getLangText = (content, targetLang = displayLanguage) => {
    if (!content) return '';
    
    // 1. Handle Object structure: { eng: "...", jp: "..." } or { en: "...", ja: "..." }
    if (typeof content === 'object') {
      const enText = content.eng || content.en || '';
      const jaText = content.jp || content.ja || '';
      
      if (targetLang === 'en') return enText;
      if (targetLang === 'jp') return jaText;
      if (targetLang === 'both') {
        if (enText && jaText) return `${enText}\n\n${jaText}`;
        return enText || jaText;
      }
      return enText || jaText;
    }

    // 2. Handle Interleaved strings: [English] content [Japanese] content
    if (typeof content === 'string') {
      const enMatch = content.match(/\[English\]\s*([\s\S]*?)(?=\[Japanese\]|$)/i);
      const jaMatch = content.match(/\[Japanese\]\s*([\s\S]*?)(?=\[English\]|$)/i);

      const enText = enMatch ? enMatch[1].trim() : '';
      const jaText = jaMatch ? jaMatch[1].trim() : '';

      if (enText || jaText) {
        if (targetLang === 'en') return enText || content;
        if (targetLang === 'jp') return jaText || content;
        if (targetLang === 'both') {
          if (enText && jaText) return `### English\n${enText}\n\n### 日本語\n${jaText}`;
          return enText || jaText || content;
        }
      }
      return content;
    }
    
    return String(content);
  };

  // Helper to extract language-specific fields from a parent object (e.g. report_markdown_en)
  const getFieldByLang = (parentObj, baseKey) => {
    if (!parentObj) return '';
    
    // Check for nested object first: parentObj[baseKey] = { eng: "...", jp: "..." }
    if (parentObj[baseKey] && typeof parentObj[baseKey] === 'object') {
      return getLangText(parentObj[baseKey]);
    }

    // Check for suffixed keys: parentObj[baseKey_en] or parentObj[baseKey_ja]
    const enKey = `${baseKey}_en`;
    const jaKey = `${baseKey}_ja`;
    const engKey = `${baseKey}_eng`;
    const jpKey = `${baseKey}_jp`;

    const enText = parentObj[engKey] || parentObj[enKey] || '';
    const jaText = parentObj[jpKey] || parentObj[jaKey] || '';

    if (displayLanguage === 'en') return enText || parentObj[baseKey] || '';
    if (displayLanguage === 'jp') return jaText || parentObj[baseKey] || '';
    if (displayLanguage === 'both') {
      if (enText && jaText) return `${enText}\n\n${jaText}`;
      return enText || jaText || parentObj[baseKey] || '';
    }
    
    return parentObj[baseKey] || '';
  };

  const formatConfidenceScore = (confidence) => {
    if (confidence === null || confidence === undefined || confidence === '') return '';
    if (typeof confidence === 'number') {
      if (confidence <= 1) return `${Math.round(confidence * 100)}%`;
      return `${Math.round(confidence)}%`;
    }
    return `${confidence}%`;
  };

  const processNestedContent = (content) => {
    if (!content) return '';
    
    // If it's already a string, check if it's JSON
    if (typeof content === 'string') {
      const trimmed = content.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const jsonData = JSON.parse(trimmed);
          
          // Detect actual content within various possible schemas
          const sourceData = jsonData.report || jsonData.facts || jsonData;
          
          let result = '';
          
          // 1. Agent Metadata Header (Bypassed as requested by user)
          /*
          const metadataObj = sourceData.agent_metadata || sourceData;
          const agentName = getLangText(metadataObj.agent_name);
          const status = getLangText(metadataObj.execution_status);
          const confidence = metadataObj.confidence_score;
          
          if (agentName || status || (confidence !== undefined && confidence !== null)) {
            result += `### ${agentName || 'Agent Intelligence Report'}\n`;
            if (status) result += `**Status:** ${status}  \n`;
            const confidenceLabel = formatConfidenceScore(confidence);
            if (confidenceLabel) result += `**Confidence Score:** ${confidenceLabel}  \n\n`;
          }
          */

          // 2. Main Markdown Report (The Core Content)
          // When report_markdown is present it already contains the complete narrative.
          // In that case we skip appending individual sub-fields to avoid duplication.
          const reportMarkdown = getFieldByLang(sourceData, 'report_markdown');
          if (reportMarkdown) {
            result += reportMarkdown + '\n\n';
            return result || content;
          }

          const MAIN_REPORT_KEYS = [
            'salesforce_analysis_report',
            'business_structure_report',
            'basic_information_report',
            'vision_analysis_report',
            'management_strategy_report',
            'management_message_report',
            'crisis_management_report',
            'dx_analysis_report',
            'competitive_landscape_report',
            'regulatory_environment_report',
            'latest_news_report',
            'financial_analysis_report',
            'account_research_report',
          ];

          for (const reportKey of MAIN_REPORT_KEYS) {
            const sectionReport = getFieldByLang(sourceData, reportKey);
            if (sectionReport) {
              result += sectionReport + '\n\n';
              break;
            }
          }

          const processBilingualTextSection = (titleEn, titleJp, value) => {
            const text = getLangText(value);
            if (!text) return '';
            if (displayLanguage === 'jp') return `#### ${titleJp}\n${text}\n\n`;
            if (displayLanguage === 'both') return `#### ${titleEn} | ${titleJp}\n${text}\n\n`;
            return `#### ${titleEn}\n${text}\n\n`;
          };

          // Salesforce / NESIC proprietary analysis sections
          result += processBilingualTextSection(
            'Executive Relationship Summary',
            'エグゼクティブ関係サマリー',
            sourceData.executive_relationship_summary
          );
          result += processBilingualTextSection('Lead Intelligence', 'リードインテリジェンス', sourceData.lead_intelligence);
          result += processBilingualTextSection(
            'Opportunity Intelligence',
            '商談インテリジェンス',
            sourceData.opportunity_intelligence
          );
          result += processBilingualTextSection('Relationship Mapping', '関係性マッピング', sourceData.relationship_mapping);
          result += processBilingualTextSection(
            'Historical NESIC Engagements',
            'NESIC過去のエンゲージメント',
            sourceData.historical_nesic_engagements
          );
          result += processBilingualTextSection('Strategic Signals', '戦略的シグナル', sourceData.strategic_signals);
          result += processBilingualTextSection(
            'Account Expansion Opportunities',
            'アカウント拡大機会',
            sourceData.account_expansion_opportunities
          );
          result += processBilingualTextSection(
            'Cross-System Correlations',
            'システム横断相関',
            sourceData.cross_system_correlations
          );
          result += processBilingualTextSection('Risk Factors', 'リスク要因', sourceData.risk_factors);
          result += processBilingualTextSection(
            'Recommended Engagement Strategy',
            '推奨エンゲージメント戦略',
            sourceData.recommended_engagement_strategy
          );

          // 3. Process Bilingual Arrays (Support both Array of Objects and Object of Arrays)
          const processBilingualArray = (titleEn, titleJp, items) => {
            if (!items) return '';
            
            let finalItems = [];
            if (Array.isArray(items)) {
              finalItems = items;
            } else if (typeof items === 'object') {
              // Handle { en: [], jp: [] } structure
              if (displayLanguage === 'jp') finalItems = items.jp || items.ja || items.en || items.eng || [];
              else if (displayLanguage === 'both') {
                const en = items.en || items.eng || [];
                const jp = items.jp || items.ja || [];
                // Merge or handle side-by-side if needed, but for now let's prioritize EN or interleaved
                if (en.length && jp.length) {
                  const combined = [];
                  const max = Math.max(en.length, jp.length);
                  for (let i = 0; i < max; i++) {
                    if (en[i]) combined.push({ eng: en[i], jp: jp[i] || '' });
                    else if (jp[i]) combined.push({ jp: jp[i] });
                  }
                  finalItems = combined;
                } else {
                  finalItems = en.length ? en : jp;
                }
              } else {
                finalItems = items.en || items.eng || items.jp || items.ja || [];
              }
            }

            if (!Array.isArray(finalItems) || finalItems.length === 0) return '';
            
            let title = titleEn;
            if (displayLanguage === 'jp') title = titleJp;
            else if (displayLanguage === 'both') title = `${titleEn} | ${titleJp}`;
            
            let section = `#### ${title}\n`;
            finalItems.forEach(item => {
              const text = getLangText(item);
              if (text) section += `- ${text}\n`;
            });
            return section + '\n';
          };

          result += processBilingualArray('Strategic Insights', '戦略的インサイト', sourceData.strategic_insights);
          result += processBilingualArray('Risk Flags', 'リスクフラグ', sourceData.risk_flags);
          result += processBilingualArray('Key Findings', '主な調査結果', sourceData.key_findings);
          
          // Additional Vision & Strategy sections
          result += processBilingualArray('Vision Themes', 'ビジョンテーマ', sourceData.vision_themes);
          result += processBilingualArray('Innovation Signals', 'イノベーションシグナル', sourceData.innovation_signals);
          result += processBilingualArray('Transformation Priorities', '変革の優先事項', sourceData.transformation_priorities);
          
          // Management Strategy specific sections
          result += processBilingualArray('Executive Priorities', '経営陣の優先事項', sourceData.executive_priorities);
          result += processBilingualArray('Growth Initiatives', '成長イニシアチブ', sourceData.growth_initiatives);
          result += processBilingualArray('Technology Priorities', 'テクノロジーの優先事項', sourceData.technology_priorities);

          // Management Message specific sections
          const posture = getLangText(sourceData.leadership_posture);
          if (posture) {
            let postureTitle = 'Leadership Posture';
            if (displayLanguage === 'jp') postureTitle = '経営姿勢';
            else if (displayLanguage === 'both') postureTitle = 'Leadership Posture | 経営姿勢';
            result += `#### ${postureTitle}\n${posture}\n\n`;
          }

          const sentiment = getLangText(sourceData.executive_sentiment);
          if (sentiment) {
            let sentimentTitle = 'Executive Sentiment';
            if (displayLanguage === 'jp') sentimentTitle = 'エグゼクティブ・センチメント';
            else if (displayLanguage === 'both') sentimentTitle = 'Executive Sentiment | エグゼクティブ・センチメント';
            result += `#### ${sentimentTitle}\n${sentiment}\n\n`;
          }

          result += processBilingualArray('Strategic Themes', '重点戦略', sourceData.strategic_themes);
          result += processBilingualArray('Transformation Signals', '変革シグナル', sourceData.transformation_signals);
          result += processBilingualArray('Risk Signals', 'リスクシグナル', sourceData.risk_signals);
          
          // Agent 8: Competitive Landscape specific sections
          result += processBilingualArray('Major Competitors', '主要な競合他社', sourceData.major_competitors);
          result += processBilingualArray('Market Pressures', '市場の圧力', sourceData.market_pressures);
          result += processBilingualArray('Innovation Gaps', 'イノベーションの課題', sourceData.innovation_gaps);
          result += processBilingualArray('Competitive Risks', '競争リスク', sourceData.competitive_risks);

          // Agent 9: Regulatory Environment specific sections
          result += processBilingualArray('Compliance Requirements', 'コンプライアンス要件', sourceData.compliance_requirements);
          result += processBilingualArray('Governance Risks', 'ガバナンスリスク', sourceData.governance_risks);
          result += processBilingualArray('Regulatory Pressures', '規制の圧力', sourceData.regulatory_pressures);
          result += processBilingualArray('Cybersecurity Obligations', 'サイバーセキュリティ義務', sourceData.cybersecurity_obligations);

          // Agent 10: Latest News specific sections
          result += processBilingualArray('Recent Events', '最近の出来事', sourceData.recent_events);
          result += processBilingualArray('Strategic Announcements', '戦略的発表', sourceData.strategic_announcements);
          result += processBilingualArray('Market Signals', '市場シグナル', sourceData.market_signals);

          // Crisis Management specific sections
          result += processBilingualArray('Identified Crises', '特定された危機', sourceData.identified_crises);
          
          const severity = getLangText(sourceData.risk_severity);
          if (severity) {
            let severityTitle = 'Risk Severity';
            if (displayLanguage === 'jp') severityTitle = 'リスクの深刻度';
            else if (displayLanguage === 'both') severityTitle = 'Risk Severity | リスクの深刻度';
            result += `#### ${severityTitle}\n${severity}\n\n`;
          }

          const responseQuality = getLangText(sourceData.executive_response_quality);
          if (responseQuality) {
            let respTitle = 'Executive Response Quality';
            if (displayLanguage === 'jp') respTitle = '経営陣の対応品質';
            else if (displayLanguage === 'both') respTitle = 'Executive Response Quality | 経営陣の対応品質';
            result += `#### ${respTitle}\n${responseQuality}\n\n`;
          }

          result += processBilingualArray('Operational Impact', '業務への影響', sourceData.operational_impact);
          result += processBilingualArray('Reputational Risks', 'レピュテーションリスク', sourceData.reputational_risks);

          // DX Analysis specific sections
          const dxScore = getLangText(sourceData.digital_transformation_score);
          if (dxScore) {
            let dxTitle = 'Digital Transformation Score';
            if (displayLanguage === 'jp') dxTitle = 'デジタルトランスフォーメーション・スコア';
            else if (displayLanguage === 'both') dxTitle = 'Digital Transformation Score | DXスコア';
            result += `#### ${dxTitle}\n${dxScore}\n\n`;
          }

          const cloudMaturity = getLangText(sourceData.cloud_maturity);
          if (cloudMaturity) {
            let cloudTitle = 'Cloud Maturity';
            if (displayLanguage === 'jp') cloudTitle = 'クラウド成熟度';
            else if (displayLanguage === 'both') cloudTitle = 'Cloud Maturity | クラウド成熟度';
            result += `#### ${cloudTitle}\n${cloudMaturity}\n\n`;
          }

          const aiReadiness = getLangText(sourceData.ai_readiness);
          if (aiReadiness) {
            let aiTitle = 'AI Readiness';
            if (displayLanguage === 'jp') aiTitle = 'AIレディネス';
            else if (displayLanguage === 'both') aiTitle = 'AI Readiness | AIレディネス';
            result += `#### ${aiTitle}\n${aiReadiness}\n\n`;
          }

          result += processBilingualArray('Modernization Gaps', 'モダナイゼーションの課題', sourceData.modernization_gaps);
          result += processBilingualArray('Technology Risks', 'テクノロジーリスク', sourceData.technology_risks);

          // 4. Structured Data (Technical Metadata)
          if (sourceData.structured_data && typeof sourceData.structured_data === 'object') {
            let structTitle = 'Technical Metadata';
            if (displayLanguage === 'jp') structTitle = 'テクニカルメタデータ';
            else if (displayLanguage === 'both') structTitle = 'Technical Metadata | テクニカルメタデータ';
            
            result += `#### ${structTitle}\n`;
            
            const processObject = (obj, indent = '') => {
              for (const [key, value] of Object.entries(obj)) {
                const formattedKey = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                if (Array.isArray(value)) {
                  result += `${indent}- **${formattedKey}:** ${value.map(v => typeof v === 'object' ? JSON.stringify(v) : v).join(', ')}\n`;
                } else if (typeof value === 'object' && value !== null) {
                  result += `${indent}- **${formattedKey}:**\n`;
                  processObject(value, indent + '  ');
                } else {
                  result += `${indent}- **${formattedKey}:** ${value}\n`;
                }
              }
            };
            
            processObject(sourceData.structured_data);
            result += '\n';
          }

          // 5. Sources
          if (Array.isArray(sourceData.web_sources_used) && sourceData.web_sources_used.length > 0) {
            let sourcesTitle = 'Sources & References';
            if (displayLanguage === 'jp') sourcesTitle = '情報源と参照先';
            else if (displayLanguage === 'both') sourcesTitle = 'Sources & References | 情報源と参照先';
            
            result += `#### ${sourcesTitle}\n`;
            sourceData.web_sources_used.forEach(source => {
              const name = getLangText(source); // Correctly resolve bilingual source name
              const url = source.url || '';
              if (url) result += `- [${name}](${url})\n`;
              else if (name) result += `- ${name}\n`;
            });
          }

          return result || content;
        } catch (e) {
          return getLangText(content);
        }
      }
      return getLangText(content);
    }
    
    return getLangText(content);
  };

  // Render markdown content using processNestedContent
  const renderMarkdownContent = (content, sectionKey = null) => {
    if (!content || content === '{}' || content.trim() === '') {
      return <div className="text-gray-500 italic">{t('researchDashboard.noContentAvailable')}</div>;
    }

    try {
      const processedContent = processNestedContent(content);

      // Auto-fix common AI markdown issues (doubled headings, duplicate lines, etc.)
      const cleanContent = sanitizeMarkdown(processedContent);

      // Log what was fixed (visible in browser DevTools console)
      if (cleanContent !== processedContent) {
        console.debug(
          `[MarkdownSanitizer]${sectionKey ? ` [${sectionKey}]` : ''}`,
          describeSanitizationDiff(processedContent, cleanContent)
        );
      }

      return (
        // Use only our custom markdown-content class – no prose double-application
        <div className={`markdown-content${hasJapaneseContent(cleanContent) ? ' ja' : ''}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              // Headings – sized for the narrow section panel, not a full page
              h1: ({ node, ...props }) => <h1 className="report-h1" {...props} />,
              h2: ({ node, ...props }) => <h2 className="report-h2" {...props} />,
              h3: ({ node, ...props }) => <h3 className="report-h3" {...props} />,
              h4: ({ node, ...props }) => <h4 className="report-h4" {...props} />,
              h5: ({ node, ...props }) => <h5 className="report-h5" {...props} />,
              h6: ({ node, ...props }) => <h6 className="report-h6" {...props} />,
              // Paragraphs
              p: ({ node, ...props }) => <p className="report-p" {...props} />,
              // Links – always open external URLs in a new tab
              a: ({ node, href, ...props }) => (
                <a
                  href={href}
                  target={href?.startsWith('http') ? '_blank' : undefined}
                  rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="report-link"
                  {...props}
                />
              ),
              // Lists
              ul: ({ node, ...props }) => <ul className="report-ul" {...props} />,
              ol: ({ node, ...props }) => <ol className="report-ol" {...props} />,
              li: ({ node, ...props }) => <li className="report-li" {...props} />,
              // Inline vs block code
              code: ({ node, inline, className, ...props }) => (
                inline
                  ? <code className="report-code-inline" {...props} />
                  : <code className="report-code-block" {...props} />
              ),
              pre: ({ node, ...props }) => <pre className="report-pre" {...props} />,
              // Tables
              table: ({ node, ...props }) => (
                <div className="report-table-wrapper">
                  <table className="report-table" {...props} />
                </div>
              ),
              thead: ({ node, ...props }) => <thead className="report-thead" {...props} />,
              tbody: ({ node, ...props }) => <tbody {...props} />,
              th: ({ node, ...props }) => <th className="report-th" {...props} />,
              td: ({ node, ...props }) => <td className="report-td" {...props} />,
              // Misc
              blockquote: ({ node, ...props }) => <blockquote className="report-blockquote" {...props} />,
              hr: ({ node, ...props }) => <hr className="report-hr" />,
              strong: ({ node, ...props }) => <strong className="report-strong" {...props} />,
              em: ({ node, ...props }) => <em className="report-em" {...props} />,
            }}
          >
            {cleanContent}
          </ReactMarkdown>
        </div>
      );
    } catch (error) {
      console.error('Error rendering markdown:', error);
      return <div className="bg-red-50 p-4 rounded border border-red-200">Error rendering content</div>;
    }
  };

  // Function to toggle view/edit mode
  const toggleViewMode = () => {
    setViewMode(prev => prev === 'view' ? 'edit' : 'view');
  };

  // Function to handle manual edits to a section
  const handleSectionEdit = (e) => {
    if (platformLanguage === 'en') {
      // When in English mode, edit the original content
      setReportSections(prev => ({
        ...prev,
        [activeSection]: e.target.value
      }));
    } else {
      // When in Japanese mode, edit the translation
      setTranslatedSections(prev => {
        const updated = { ...prev };
        if (!updated[activeSection]) {
          updated[activeSection] = {};
        }
        updated[activeSection]['ja'] = e.target.value;
        return updated;
      });
    }
  };

  // Function to copy current section to clipboard
  const copySection = () => {
    navigator.clipboard.writeText(getSectionContent(activeSection));
    // Could add a notification here
  };

  // New improved editing functions

  // Function to start editing a section
  const startEditingSection = () => {
    const currentContent = getCurrentSectionContent();
    const cleanContent = extractCleanMarkdown(currentContent);
    setEditedContent(cleanContent);
    setIsEditMode(true);
    setHasUnsavedChanges(false);
  };

  // Function to cancel editing
  const cancelEditing = () => {
    setIsEditMode(false);
    setEditedContent('');
    setHasUnsavedChanges(false);
  };

  // Function to get current section content based on language
  const getCurrentSectionContent = () => {
    if (platformLanguage === 'ja') {
      return translatedSections[activeSection]?.ja || reportSections[activeSection] || '';
    }
    return reportSections[activeSection] || '';
  };

  // Function to extract clean markdown from potentially JSON-wrapped content
  const extractCleanMarkdown = (content) => {
    if (!content) return '';

    // If content looks like JSON, try to extract the markdown
    if (content.trim().startsWith('{') && content.trim().endsWith('}')) {
      try {
        const parsed = JSON.parse(content);
        const keys = Object.keys(parsed);
        if (keys.length === 1) {
          const singleValue = parsed[keys[0]];
          if (typeof singleValue === 'string') {
            return singleValue;
          }
        }

        const rendered = processNestedContent(content);
        if (rendered && rendered.trim() && rendered !== content) {
          return rendered;
        }

        // Last resort: convert top-level keys to markdown sections
        return Object.entries(parsed)
          .map(([key, value]) => {
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            const text = typeof value === 'object' ? getLangText(value) : String(value);
            return `## ${formattedKey}\n\n${text}\n\n`;
          })
          .join('');
      } catch (e) {
        // If parsing fails, return as is
        return content;
      }
    }

    return content;
  };

  // Function to handle content changes during editing
  const handleContentChange = (e) => {
    setEditedContent(e.target.value);
    setHasUnsavedChanges(true);
  };

  // Function to save section changes
  const saveSectionChanges = async () => {
    if (!editedContent.trim()) {
      toast.error(t('researchDashboard.errors.emptyContent'));
      return;
    }

    setIsSaving(true);

    try {
      // Determine the correct API endpoint based on language
      const endpoint = platformLanguage === 'ja'
        ? `api/dashboards/${dashboardId}/sections/japanese/${activeSection}`
        : `api/dashboards/${dashboardId}/sections/${activeSection}`;

      const response = await authenticatedFetch(
        endpoint,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            content: editedContent
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save section');
      }

      // Exit edit mode first
      setIsEditMode(false);
      setEditedContent('');
      setHasUnsavedChanges(false);

      // Show success message
      toast.success(t('researchDashboard.sectionSaved'));

      // Refresh sections data from backend to get the latest content
      console.log('Refreshing sections after save...');
      const refreshResult = await fetchSections();

      if (refreshResult.success) {
        console.log('Successfully refreshed sections after save');

        // Update section states to reflect the refreshed data
        const updatedSectionStates = {};
        Object.entries(refreshResult.sections).forEach(([key, content]) => {
          const hasContent = content && content !== '{}' && content.trim() !== '';
          updatedSectionStates[key] = {
            content: content,
            isLoading: false,
            lastUpdated: new Date().toISOString(),
            hasData: hasContent,
            status: hasContent ? 'complete' : 'pending',
            error: null,
            progressPercentage: hasContent ? 100 : 0
          };
        });

        setSectionStates(updatedSectionStates);

        // Update completion flags based on language
        if (platformLanguage === 'ja') {
          setIsJapaneseComplete(refreshResult.isComplete);
          if (refreshResult.isComplete) {
            localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
          }
        } else {
          setIsEnglishComplete(refreshResult.isComplete);
          if (refreshResult.isComplete) {
            localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'true');
          }
        }
      } else {
        console.warn('Failed to refresh sections after save:', refreshResult.error);
        // Fallback: Update local state with saved content
        if (platformLanguage === 'ja') {
          setTranslatedSections(prev => ({
            ...prev,
            [activeSection]: {
              ...prev[activeSection],
              ja: editedContent
            }
          }));
        } else {
          setReportSections(prev => ({
            ...prev,
            [activeSection]: editedContent
          }));
        }

        // Update section state to reflect the save
        setSectionStates(prev => ({
          ...prev,
          [activeSection]: {
            ...prev[activeSection],
            lastUpdated: new Date().toISOString(),
            content: editedContent
          }
        }));
      }

    } catch (error) {
      console.error('Error saving section:', error);
      toast.error(error.message || t('researchDashboard.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // Function to refresh data
  const refreshData = async () => {
    logger.info('Refreshing dashboard data');

    if (!dashboardId) {
      console.warn('[refreshData] dashboardId is undefined, skipping refresh');
      return;
    }

    // Early exit if research is already complete - no need for authorization check
    const isResearchComplete = localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`) === 'true';
    const researchCurrentStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);

    if (isResearchComplete || researchCurrentStatus === 'COMPLETED') {
      console.log('[refreshData] Research is already complete, skipping refresh and authorization check');
      return;
    }

    // No authorization check needed during refresh - backend polling handles auth
    // Authorization was already validated when research started
    console.log('[refreshData] Proceeding with refresh (auth handled by polling endpoints)');

    // Check for pending tasks
    await checkPendingProcess();

    // Check if research is already complete to avoid restarting polling unnecessarily
    const isComplete = localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`) === 'true';
    const currentStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);

    // If research is already complete, don't restart polling
    if (isComplete || currentStatus === RESEARCH_STATUS.COMPLETED) {
      console.log('Not restarting polling in refreshData because research is already complete');
      return;
    }

    // Only restart polling if we have all required flags for an active research
    const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
    const researchStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);
    const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';
    const storedCompanyName = localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`);

    // Only proceed with refresh if we have an active research or existing content
    const hasExistingContent = Object.values(reportSections).some(
      content => content && content !== '{}' && content.trim() !== ''
    );

    if (pendingTaskId &&
      isResearchInProgress &&
      researchStatus === RESEARCH_STATUS.IN_PROGRESS &&
      !pollingIntervalRef.current) {
      console.log('Found active research in progress, restarting polling');
      startPollingWithControls(pendingTaskId);
    } else if (!isProcessing && storedCompanyName && hasExistingContent) {
      // Only fetch latest sections if we have existing content
      console.log('Fetching latest sections for company with existing content:', storedCompanyName);
      const fetchResult = await fetchAllSections(storedCompanyName);

      if (fetchResult.success) {
        setReportSections(normalizeReportSections(fetchResult.sections));
        setReportGenerated(true);

        // Auto-select first section if none selected
        if (shouldAutoSelectSection()) {
          const orderedKeys = getOrderedSectionKeys(fetchResult.sections);
          if (orderedKeys.length > 0) {
            applyActiveSection(orderedKeys[0]);
          }
        }
      } else {
        logger.warn('Failed to fetch latest sections during refresh');
      }
    } else {
      console.log('Skipping refresh - no active research or existing content');
    }
  };

  // Helper function to get section title
  const getSectionTitle = (sectionKey) => {
    // Map backend keys to translation keys
    const translationKeyMapping = {
      'basic_information': 'basic',
      'business_structure': 'business_structure',
      'vision_analysis': 'vision',
      'management_strategy': 'management',
      'management_message': 'management_message',
      'crisis_management': 'crisis_management',
      'dx_analysis': 'dx',
      'financial_analysis': 'financial',
      'competitive_landscape': 'competitive',
      'regulatory_environment': 'regulatory',
      'salesforce_analysis': 'salesforce',
      'latest_news': 'news',
      'account_research': 'account_strategy'
    };

    // Get the translation key
    const translationKey = translationKeyMapping[sectionKey] || sectionKey;

    // Use translations for section titles
    return t(`researchDashboard.sections.${translationKey}`) || sectionKey;
  };
  const userEmail = keycloak.tokenParsed?.email || '';
  const sendEmailNotification = async (taskId) => {
    if (!taskId) {
      logger.warn('No task ID provided for email notification');
      return;
    }

    // Check if notification was already sent for this task
    const notificationKey = `dashboard_${dashboardId}_notification_sent_${taskId}`;
    if (localStorage.getItem(notificationKey) === 'true') {
      logger.info('Email notification already sent for task:', taskId);
      return;
    }

    try {
      const response = await authenticatedFetch(
        'api/execute-draft-skills',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: keycloak.tokenParsed?.email || '',
            task_id: taskId
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send email notification');
      }

      // Mark this notification as sent
      localStorage.setItem(notificationKey, 'true');
      logger.info('Email notification sent successfully for task:', taskId);
    } catch (error) {
      logger.error('Error sending email notification:', error);
      // Don't mark as sent if it failed
    }
  };
  // Function to check for pending processes
  const checkPendingProcess = async () => {
    if (isCheckingStatus) {
      logger.info('Status check already in progress, skipping');
      return;
    }

    if (!dashboardId) {
      console.warn('[checkPendingProcess] dashboardId is undefined, skipping check');
      return;
    }

    try {
      setIsCheckingStatus(true);

      // Get pending task details from localStorage
      const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
      const researchStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);
      const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';
      const storedCompanyName = localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`);

      // If no pending task or research is already complete, make sure isProcessing is cleared
      if (!pendingTaskId || researchStatus === RESEARCH_STATUS.COMPLETED) {
        // Ensure processing state is cleared even if research is marked complete in localStorage
        setIsProcessing(false);
        setResearchInProgress(false);
        setIsCheckingStatus(false);
        return;
      }

      console.log('Found pending task:', pendingTaskId, 'with status:', researchStatus);

      // Check if all sections are complete by fetching them
      console.log('Checking if all sections are complete');
      const fetchResult = await fetchAllSections(storedCompanyName);

      // If all 13 sections are present and have content, mark as complete
      if (fetchResult.success && fetchResult.allSectionsPresent) {
        console.log('All 13 sections are present and have content, marking research as complete');

        // Stop polling first to prevent race conditions
        stopPolling(true);

        // Update states and localStorage
        setIsProcessing(false);
        setProgressPercentage(100);
        setProcessingStep('researchDashboard.complete');
        setResearchStatus(RESEARCH_STATUS.COMPLETED);
        setResearchInProgress(false);
        setIsProcessComplete(true);

        localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.COMPLETED);
        localStorage.setItem(`dashboard_${dashboardId}_progressPercentage`, '100');
        localStorage.setItem(`dashboard_${dashboardId}_researchInProgress`, 'false');
        localStorage.setItem(`dashboard_${dashboardId}_isProcessComplete`, 'true');

        // Set sections and mark as generated
        setReportSections(normalizeReportSections(fetchResult.sections));
        setReportGenerated(true);

        // Add completion message to chat only if we haven't shown it already
        const completionMessageShown = localStorage.getItem(`dashboard_${dashboardId}_completionMessageShown`) === 'true';
        if (!completionMessageShown) {
          addMessage('ai', null, {
            i18nKey: 'researchDashboard.aiMessages.researchCompleted',
            i18nParams: { company: storedCompanyName }
          });
          localStorage.setItem(`dashboard_${dashboardId}_completionMessageShown`, 'true');
        }
      }
    } catch (error) {
      console.error('Error checking pending process:', error);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  // Function to handle visibility changes (switching tabs/windows)
  const handleVisibilityChange = () => {
    if (document.hidden) {
      console.log('Page hidden, pausing polling');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Only proceed if we're coming back to visible state
    const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
    const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';
    const initialResearchLanguage = localStorage.getItem(`dashboard_${dashboardId}_initial_research_language`);

    console.log('Visibility changed to visible:', {
      pendingTaskId,
      isResearchInProgress,
      initialResearchLanguage,
      currentPlatformLanguage: platformLanguage
    });

    // Only resume if we have all required information
    if (pendingTaskId && isResearchInProgress && initialResearchLanguage) {
      console.log('Resuming polling with initial research language:', initialResearchLanguage);

      // Force the initial research language, ignoring current platform language
      startPollingWithControls(pendingTaskId, initialResearchLanguage);
    } else {
      console.log('Not resuming polling - missing required information:', {
        hasPendingTask: !!pendingTaskId,
        isInProgress: isResearchInProgress,
        hasInitialLanguage: !!initialResearchLanguage
      });
    }
  };

  // Update the cancelResearch function to work with our new polling system
  const cancelResearch = async () => {
    try {
      logger.info('Cancelling research process');

      // Stop all polling immediately
      stopPolling(true);

      // Get the pending task ID and current research status
      const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
      const currentStatus = researchStatus || localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);

      // Only call the cancel API if the task is actually running (not failed, completed, or not started)
      if (pendingTaskId && currentStatus &&
        currentStatus !== RESEARCH_STATUS.FAILED &&
        currentStatus !== RESEARCH_STATUS.COMPLETED &&
        currentStatus !== RESEARCH_STATUS.NOT_STARTED) {

        logger.info(`Attempting to cancel running task ${pendingTaskId} with status: ${currentStatus}`);

        try {
          // Call the API to cancel the research task
          const response = await authenticatedFetch(
            `/api/research/${dashboardId}/cancel-research-tasks/${pendingTaskId}`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json'
              }
            },
            keycloak.token
          );

          if (!response.ok && response.status !== 404) {
            throw new Error(`Failed to cancel research task: ${response.statusText}`);
          }

          logger.info('Successfully cancelled running research task');
        } catch (apiError) {
          logger.warn('Failed to cancel via API (task may already be finished):', apiError.message);
          // Continue with cleanup even if API call fails
        }
      } else {
        logger.info(`Skipping API cancellation for task with status: ${currentStatus} (performing cleanup only)`);
      }

      // Reset all states (this works for all statuses including failed)
      setIsProcessing(false);
      setIsCheckingStatus(false);
      setProcessingStep('');
      setProgressPercentage(0);
      setProcessingTimeLeft(0);
      setReportGenerated(false);
      setShowSubsidiaryChoice(false);
      setSubsidiaries([]);
      localStorage.removeItem(`dashboard_${dashboardId}_subsidiaries`);
      localStorage.removeItem(`dashboard_${dashboardId}_showSubsidiaryChoice`);
      setResearchStatus(RESEARCH_STATUS.NOT_STARTED);
      setResearchInProgress(false);
      setIsProcessComplete(false);
      setReportSections({});
      setSectionStates({});
      clearActiveSectionSelection();
      localStorage.removeItem(`dashboard_${dashboardId}_activeSection`);
      setIsEnglishComplete(false);
      setIsJapaneseComplete(false);

      // Clear any already-generated report content from backend
      try {
        const clearResponse = await authenticatedFetch(
          `/api/dashboards/${dashboardId}/clear-all`,
          { method: 'DELETE' },
          keycloak.token
        );
        if (!clearResponse.ok && clearResponse.status !== 404) {
          logger.warn(`Failed to clear dashboard data after cancel: ${clearResponse.statusText}`);
        }
      } catch (clearError) {
        logger.warn('Failed to clear dashboard data after cancel:', clearError.message);
      }

      // Clear localStorage items
      clearPendingTaskData();
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(`dashboard_${dashboardId}_`)) {
          // Keep chat messages so cancel message stays visible; wipe research/report keys
          if (key === `dashboard_${dashboardId}_messages`) return;
          localStorage.removeItem(key);
        }
      });

      // Add appropriate message based on the original status
      await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.researchCancelled'
      });

      // Reset the UI state
      setShowStartButton(true);
      setDashboardDataExists(false);

      logger.info('Research process cancelled/cleared successfully');
    } catch (error) {
      logger.error('Error cancelling research:', error);
      toast.error(t('researchDashboard.errors.cancelError'));
    }
  };

  // Add lifecycle hooks for visibility and component mounting/unmounting
  useEffect(() => {
    // Check for pending process on component mount
    checkPendingProcess();

    // Set up visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up on component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
    };
  }, [dashboardId, keycloak.token]);

  // Clean up polling interval on component unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // Helper function to clear pending task data
  const clearPendingTaskData = () => {
    if (dashboardId) {
      // Clear all the research-related localStorage items
      const keysToRemove = [
        `dashboard_${dashboardId}_pendingTaskId`,
        `dashboard_${dashboardId}_pendingCompany`,
        `dashboard_${dashboardId}_selectedSubsidiary`,
        `dashboard_${dashboardId}_researchInProgress`,
        `dashboard_${dashboardId}_researchStatus`,
        `dashboard_${dashboardId}_progressPercentage`,
        `dashboard_${dashboardId}_isProcessing`,
        `dashboard_${dashboardId}_pollingStartTime`,
        `dashboard_${dashboardId}_lastPollingTime`,
        `dashboard_${dashboardId}_polling_language`,
        `dashboard_${dashboardId}_isProcessComplete`,
        `dashboard_${dashboardId}_completionMessageShown`
      ];

      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log('Cleared all pending task data from localStorage');
    }
  };

  // Add this effect at the component level
  useEffect(() => {
    // Check for pending processes on component mount
    checkPendingProcess();
  }, [dashboardId, keycloak.token]); // Add dependencies

  // Modified checkCompletedResearch to not show success message
  const checkCompletedResearch = async () => {
    if (!dashboardId || !keycloak.token) {
      logger.info('Missing dashboard ID or not authenticated, skipping completed research check');
      return;
    }

    // If research is already generated and we're not processing, skip the check
    if (reportGenerated && !isProcessing) {
      logger.info('Research already generated and not processing, skipping check');
      return;
    }

    try {
      const { authenticatedFetch } = await import('../utils/auth');

      const dashboardResponse = await authenticatedFetch(
        `api/dashboards/${dashboardId}`,
        {
          method: 'GET'
        },
        keycloak.token
      );

      if (!dashboardResponse.ok) {
        throw new Error(`Failed to get dashboard details: ${dashboardResponse.statusText}`);
      }

      const dashboardData = await dashboardResponse.json();
      // logger.info('Dashboard data fetched for completed research check', dashboardData);

      if (dashboardData && dashboardData.name) {
        setTitle(dashboardData.name);
      }

      if (dashboardData && dashboardData.name) {
        // const companyName = dashboardData.name;
        // // setSelectedCompany(companyName);
        // setCompany(companyName);
        // localStorage.setItem(`dashboard_${dashboardId}_selectedCompany`, companyName);

        // First check Japanese sections
        // const japaneseResponse = await authenticatedFetch(
        //   `api/dashboards/${dashboardId}/sections/japanese`,
        //   {
        //     method: 'GET'
        //   },
        //   keycloak.token
        // );

        // if (japaneseResponse.ok) {
        //   const japaneseData = await japaneseResponse.json();
        //   // Check if Japanese data exists and is not empty
        //   const hasJapaneseData = Object.entries(japaneseData).some(([key, value]) => {
        //     return typeof value === 'string' && value !== "{}" && value.trim() !== '';
        //   });

        //   if (hasJapaneseData) {
        //     logger.info('Found existing Japanese research');
        //     setIsJapaneseComplete(true);
        //     setIsProcessing(false);
        //     localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
        //   }
        // }

        // Then check the appropriate sections based on current platform language
        const sectionsEndpoint = platformLanguage === 'ja'
          ? `api/dashboards/${dashboardId}/sections/japanese`
          : `api/dashboards/${dashboardId}/sections`;

        const sectionsResponse = await authenticatedFetch(
          sectionsEndpoint,
          {
            method: 'GET'
          },
          keycloak.token
        );

        if (sectionsResponse.ok) {
          const sectionsData = await sectionsResponse.json();

          if (sectionsData && Object.keys(sectionsData).length > 0 &&
            Object.values(sectionsData).some(content => content && content.trim() !== '')) {

            logger.info('Found existing research sections for this dashboard', {
              sectionsCount: Object.keys(sectionsData).length
            });

            const processedSections = {};
            for (const [key, content] of Object.entries(sectionsData)) {
              processedSections[key] = content;
            }

            setReportSections(normalizeReportSections(processedSections));
            setReportGenerated(true);
            setIsProcessing(false);
            setIsLoading(false);
            localStorage.setItem(`dashboard_${dashboardId}_reportGenerated`, 'true');
            localStorage.setItem(`dashboard_${dashboardId}_isProcessing`, 'false');
            // Set English generated flag if we're viewing English content
            if (platformLanguage === 'en') {
              localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'true');
              setIsEnglishComplete(true);
            }
            if (platformLanguage === 'ja') {
              localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
              setIsJapaneseComplete(true);
            }
            // Auto-select first section only if no section is currently active
            if (shouldAutoSelectSection()) {
              const orderedKeys = getOrderedSectionKeys(processedSections);
              if (orderedKeys.length > 0) {
                applyActiveSection(orderedKeys[0]);
              }
            }

          } else {
            logger.info('No existing research sections found, dashboard needs research process to be started');
            setShowStartButton(true);
          }
        } else {
          logger.warn('No sections found for this dashboard, research likely not completed yet');
          setShowStartButton(true);
        }
      } else {
        logger.info('Dashboard has no company name, research has not been started yet');
        setShowStartButton(true);
      }
    } catch (error) {
      logger.error('Error checking for completed research:', error);
      setShowStartButton(true);
    }
  };

  /**
   * Check if user has permission to edit this dashboard
   * @returns {boolean} True if user has edit permission
   */
  const canEdit = useCallback(() => {
    if (!dashboard) return false;
    return isOwner(dashboard) || hasWriteAccess(dashboard);
  }, [dashboard, isOwner, hasWriteAccess]);

  /**
   * Check if user has permission to view this dashboard
   * @returns {boolean} True if user has view permission
   */
  const canView = useCallback(() => {
    if (!dashboard) return false;
    return isOwner(dashboard) || hasReadAccess(dashboard);
  }, [dashboard, isOwner, hasReadAccess]);

  // Fetch dashboard details early in the component lifecycle
  useEffect(() => {
    const fetchDashboardDetails = async () => {
      if (!dashboardId || !keycloak.authenticated) return;

      try {
        const response = await authenticatedFetch(
          `api/dashboards/${dashboardId}`,
          { method: 'GET' },
          keycloak.token
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch dashboard: ${response.status}`);
        }

        const data = await response.json();
        setDashboard(data);
        setDashboardLoaded(true);
      } catch (error) {
        console.error('Error fetching dashboard details:', error);
        toast.error(t('common.error'));
      }
    };

    fetchDashboardDetails();
  }, [dashboardId, keycloak.authenticated, keycloak.token, t]);

  // Update the startResearchProcess function to check permissions
  const startResearchProcess = async (companyNameToUse, subsidiary = null, options = {}) => {
    try {
      // **NEW**: Log localStorage values at the start of research process
      console.log('=== START RESEARCH PROCESS ===');
      console.log('Company name:', companyNameToUse);
      console.log('Subsidiary:', subsidiary);
      console.log('Current platform language:', platformLanguage);
      console.log('Dashboard ID:', dashboardId);
      console.log('Initial localStorage values:');
      console.log('  - researchInProgress:', localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`));
      console.log('  - pendingTaskId:', localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`));
      console.log('  - is_japanese_complete:', localStorage.getItem(`dashboard_${dashboardId}_is_japanese_complete`));
      console.log('  - is_english_generated:', localStorage.getItem(`dashboard_${dashboardId}_is_english_generated`));
      console.log('  - selectedCompany:', localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`));
      console.log('  - platformLanguage:', localStorage.getItem(`dashboard_${dashboardId}_platformLanguage`));
      console.log('  - initial_research_language:', localStorage.getItem(`dashboard_${dashboardId}_initial_research_language`));
      console.log('  - researchStatus:', localStorage.getItem(`dashboard_${dashboardId}_researchStatus`));
      console.log('  - isProcessComplete:', localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`));

      setIsProcessing(true);
      setResearchInProgress(true);

      // Bilingual research: poll the English endpoint; JP sections are saved in parallel.
      const initialResearchLanguage = 'en';
      localStorage.setItem(`dashboard_${dashboardId}_initial_research_language`, initialResearchLanguage);
      localStorage.setItem(`dashboard_${dashboardId}_reportLanguage`, REPORT_GENERATION_LANGUAGE);
      console.log('=== SETTING INITIAL RESEARCH LANGUAGE ===');
      console.log('Setting initial research language to:', initialResearchLanguage);

      if (!canEdit()) {
        toast.error(t('dashboard.noEditPermission'));
        return;
      }

      if (!companyNameToUse) {
        logger.warn('No company name provided');
        throw new Error('Company name is required');
      }

      // Set initial states
      setCompanyName(companyNameToUse);
      setCompany(companyNameToUse);
      setSelectedCompany(companyNameToUse);
      localStorage.setItem(`dashboard_${dashboardId}_company`, companyNameToUse);
      localStorage.setItem(`dashboard_${dashboardId}_selectedCompany`, companyNameToUse);

      console.log('=== SETTING RESEARCH STATES ===');
      console.log('Setting company states to:', companyNameToUse);

      // Set research state
      setResearchStatus(RESEARCH_STATUS.IN_PROGRESS);
      setResearchInProgress(true);
      setIsLoading(true);
      setIsProcessing(true);
      setProcessingStep('researchDashboard.processingSteps.initializingResearch');
      setProgressPercentage(0);

      // **CRITICAL**: Update localStorage state BEFORE starting polling
      // This ensures polling reads the correct values
      localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.IN_PROGRESS);
      localStorage.setItem(`dashboard_${dashboardId}_researchInProgress`, 'true');
      localStorage.setItem(`dashboard_${dashboardId}_isProcessing`, 'true');
      localStorage.setItem(`dashboard_${dashboardId}_progressPercentage`, '0');

      // **CRITICAL**: Clear completion flags to ensure polling doesn't think research is done
      localStorage.removeItem(`dashboard_${dashboardId}_isProcessComplete`);
      localStorage.removeItem(`dashboard_${dashboardId}_completionMessageShown`);

      console.log('=== UPDATED LOCALSTORAGE STATES ===');
      console.log('Updated localStorage values:');
      console.log('  - researchStatus:', localStorage.getItem(`dashboard_${dashboardId}_researchStatus`));
      console.log('  - researchInProgress:', localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`));
      console.log('  - isProcessing:', localStorage.getItem(`dashboard_${dashboardId}_isProcessing`));
      console.log('  - isProcessComplete:', localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`));
      console.log('  - initial_research_language:', localStorage.getItem(`dashboard_${dashboardId}_initial_research_language`));
      console.log('  - selectedCompany:', localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`));

      // Reset completion message shown flag
      localStorage.removeItem(`dashboard_${dashboardId}_completionMessageShown`);

      // New research run — allow first auto-select again
      clearActiveSectionSelection();
      localStorage.removeItem(`dashboard_${dashboardId}_activeSection`);

      // Initialize all sections with loading state
      initializeSectionStates();

      // Show sections UI immediately
      setReportGenerated(true);
      localStorage.setItem(`dashboard_${dashboardId}_reportGenerated`, 'true');

      await addMessage('user', `${companyNameToUse}`);
      await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.startingResearch',
        i18nParams: { company: companyNameToUse }
      });

      console.log('=== MAKING API CALL TO START RESEARCH ===');
      console.log('API call parameters:');
      console.log('  - company_name:', companyNameToUse);
      console.log('  - language:', AGENT_API_LANGUAGE);
      console.log('  - stock_ticker:', subsidiary ? subsidiary.ticker : null);
      console.log('  - industry:', subsidiary ? subsidiary.industry : null);

      // Make API call to start research process
      const processResponse = await authenticatedFetch(
        `/api/research/${dashboardId}/start-research`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            company_name: companyNameToUse,
            language: AGENT_API_LANGUAGE,
            stock_ticker: stockTicker || (subsidiary ? subsidiary.ticker : null) || null,
            industry: industry || (subsidiary ? subsidiary.industry : null) || null
          })
        },
        keycloak.token
      );

      if (!processResponse.ok) {
        throw new Error(`Failed to start research process: ${processResponse.statusText}`);
      }

      const processResult = await processResponse.json();
      console.log('=== API RESPONSE RECEIVED ===');
      console.log('Process result:', processResult);

      const brokenIntegrations = processResult.integrations?.needs_reconnect || [];
      if (brokenIntegrations.length > 0) {
        const names = brokenIntegrations.map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ');
        toast.error(
          `${names} must be reconnected by ${processResult.integrations.owner_email} in Integrations. This research runs without that data.`,
          { duration: 10000 }
        );
      }

      if (processResult.task_id) {
        const taskId = processResult.task_id;
        console.log('=== RECEIVED TASK ID ===');
        console.log('Task ID:', taskId);

        // Store the task ID in localStorage
        localStorage.setItem(`dashboard_${dashboardId}_pendingTaskId`, taskId);
        localStorage.setItem(`dashboard_${dashboardId}_pendingTimestamp`, new Date().toISOString());

        console.log('=== STORED TASK ID IN LOCALSTORAGE ===');
        console.log('Stored pendingTaskId:', localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`));
        console.log('About to start polling with controls...');

        // Start centralized polling with the task ID
        startPollingWithControls(taskId);

        // Hide loading indicator
        setIsLoading(false);

        console.log('=== RESEARCH PROCESS STARTED SUCCESSFULLY ===');

        return {
          success: true,
          taskId
        };
      } else {
        throw new Error('No task ID received from server');
      }
    } catch (error) {
      logger.error('Error in startResearchProcess:', error);

      // Reset state on error
      setIsLoading(false);
      setIsProcessing(false);
      setResearchInProgress(false);
      setResearchStatus(RESEARCH_STATUS.FAILED);

      // Update localStorage
      localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.FAILED);
      localStorage.setItem(`dashboard_${dashboardId}_researchInProgress`, 'false');
      localStorage.setItem(`dashboard_${dashboardId}_isProcessing`, 'false');

      // Show error message
      setProcessingStep(t('researchDashboard.aiMessages.errorGenerating', { message: error.message }));
      await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.errorGenerating',
        i18nParams: { message: error.message }
      });

      return {
        success: false,
        error: error.message
      };
    }
  };

  // Add new polling function
  const startPolling = (taskId) => {
    // This function is deprecated. Use startPollingWithControls instead.
    // Redirecting to new function for backward compatibility
    console.log('Old startPolling function called - redirecting to startPollingWithControls');
    startPollingWithControls(taskId);
  };

  // Add cleanup effect for polling - this is redundant with our main cleanup
  useEffect(() => {
    return () => {
      console.log('Cleaning up polling intervals');
      stopPolling();
    };
  }, []);

  // Add effect to resume polling when returning to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
      const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';

      console.log('Visibility changed:', {
        isVisible: !document.hidden,
        pendingTaskId,
        isResearchInProgress
      });

      if (!document.hidden && pendingTaskId && isResearchInProgress) {
        console.log('Page visible again, resuming polling for in-progress research');
        setIsProcessing(true);
        startPollingWithControls(pendingTaskId);
      } else if (document.hidden) {
        // Just pause polling but don't reset state when page is hidden
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check for in-progress research on mount
    const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
    const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';

    if (pendingTaskId && isResearchInProgress) {
      console.log('Found in-progress research on mount, resuming polling');
      setIsProcessing(true);
      startPollingWithControls(pendingTaskId);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dashboardId]);

  // Updated fetchLatestSections function with better error handling
  const fetchLatestSections = async (retryCount = 0) => {
    console.log('[fetchLatestSections] Starting with dashboardId:', dashboardId);
    console.log('[fetchLatestSections] Platform language:', platformLanguage);
    if (!dashboardId) {
      console.error('[fetchLatestSections] dashboardId is undefined!');
      return { success: false, error: 'Dashboard ID is undefined' };
    }

    try {
      const endpoint = platformLanguage === 'ja'
        ? `api/dashboards/${dashboardId}/sections/japanese`
        : `api/dashboards/${dashboardId}/sections`;

      console.log('[fetchLatestSections] Using endpoint:', endpoint);

      const response = await authenticatedFetch(
        endpoint,
        { method: 'GET' },
        keycloak.token
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.statusText}`);
      }

      const sections = await response.json();
      console.log('Received sections update:', Object.keys(sections));

      // Track which sections were updated this cycle
      const updatedSections = [];
      let allSectionsValid = true;

      // Process each section individually and update state if content has changed
      Object.entries(sections).forEach(([key, content]) => {
        const hasValidContent = content && content !== '{}' && content.trim() !== '';

        if (hasValidContent) {
          console.log(`Updating section ${key} with new content`);

          // Check if this is a newly completed section (was loading, now has content)
          const wasLoading = sectionStates[key]?.status === 'loading' || 
                            sectionStates[key]?.status === undefined ||
                            !sectionStates[key]?.hasData;
          const isNewlyCompleted = wasLoading && hasValidContent;

          if (platformLanguage === 'ja') {
            setTranslatedSections(prev => ({
              ...prev,
              [key]: {
                ...(prev[key] || {}),
                ja: content
              }
            }));
            // Also update reportSections to ensure tabs know this section exists
            setReportSections(prevSections => ({
              ...prevSections,
              [key]: prevSections[key] || '' // Keep existing English content or empty string
            }));
            ensureSectionKeys({ [key]: content });
          } else {
            // Update reportSections for this section
            setReportSections(prevSections => ({
              ...prevSections,
              [key]: content
            }));
          }

          // Update section state to show it's ready
          setSectionStates(prevStates => ({
            ...prevStates,
            [key]: {
              content: content,
              isLoading: false,
              lastUpdated: new Date().toISOString(),
              hasData: true,
              status: 'complete',
              progressPercentage: 100
            }
          }));


          
          // Track ALL updated sections (not just newly completed) for UI refresh
          updatedSections.push(key);

          // Log the update
          console.log(`Section ${key} marked as complete with valid content`);
        } else {
          // Update loading state for sections without content
          setSectionStates(prevStates => ({
            ...prevStates,
            [key]: {
              ...prevStates[key],
              isLoading: true,
              status: 'loading',
              progressPercentage: prevStates[key]?.progressPercentage || 0
            }
          }));
          console.log(`Section ${key} still loading - no valid content yet`);
        }
      });

      // Record the time of last successful polling
      setLastPollingTime(new Date().toISOString());
      localStorage.setItem(`dashboard_${dashboardId}_lastPollingTime`, new Date().toISOString());

      // If we updated any sections, refresh the UI
      if (updatedSections.length > 0) {
        setContentRefreshKey(prev => prev + 1);
        console.log(`Updated ${updatedSections.length} sections in this polling cycle`);
        
        // Auto-select the first completed section if no section is currently selected
        if (shouldAutoSelectSection() && updatedSections.length > 0) {
          const firstCompletedSection = updatedSections[0];
          console.log(`Auto-selecting first completed section: ${firstCompletedSection}`);
          applyActiveSection(firstCompletedSection);
        }
      }

      return {
        success: true,
        updatedSections,
        allSectionsValid
      };
    } catch (error) {
      console.error('Error fetching latest sections:', error);

      // Update error state for all sections
      setSectionStates(prevStates => {
        const updatedStates = { ...prevStates };
        Object.keys(updatedStates).forEach(key => {
          if (updatedStates[key].status === 'loading') {
            updatedStates[key].error = error.message;
          }
        });
        return updatedStates;
      });

      // Implement retry logic
      if (retryCount < 3) {
        console.log(`Retrying fetch (attempt ${retryCount + 1})...`);
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(fetchLatestSections(retryCount + 1));
          }, 2000); // Wait 2 seconds before retrying
        });
      }

      return {
        success: false,
        error: error.message
      };
    }
  };

  // First, update fetchAllSections to remove localStorage usage and always fetch from API
  const fetchAllSections = useCallback(async (companyName) => {
    console.log('[fetchAllSections] Starting with params:', {
      companyName,
      dashboardId,
      platformLanguage
    });

    if (!dashboardId) {
      console.warn('[fetchAllSections] dashboardId is undefined, skipping fetch');
      return { success: false, message: "Missing dashboard ID" };
    }

    try {
      if (!companyName) {
        console.log("[fetchAllSections] Cannot fetch sections, missing companyName");
        return { success: false, message: "Missing company name" };
      }

      // Use the current platform language to determine the endpoint
      const endpoint = platformLanguage === 'ja'
        ? `api/dashboards/${dashboardId}/sections/japanese`
        : `api/dashboards/${dashboardId}/sections`;

      console.log('[fetchAllSections] Using endpoint:', endpoint);

      const response = await authenticatedFetch(
        endpoint,
        { method: 'GET' },
        keycloak.token
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.statusText}`);
      }

      const sectionsData = await response.json();
      console.log(`Received sections data with ${Object.keys(sectionsData).length} sections`);

      // Process the sections to ensure they match our required format
      const processedSections = {};
      let allSectionsPresent = true;

      REQUIRED_SECTIONS.forEach(sectionKey => {
        const hasValidContent = sectionsData[sectionKey] &&
          sectionsData[sectionKey] !== '{}' &&
          sectionsData[sectionKey].trim() !== '';

        if (hasValidContent) {
          processedSections[sectionKey] = sectionsData[sectionKey];
        } else {
          processedSections[sectionKey] = '';
          allSectionsPresent = false;
        }
      });

      // Count completed sections for progress calculation
      const completedSections = Object.values(processedSections).filter(content =>
        content && content !== '{}' && content.trim() !== ''
      ).length;

      // Calculate progress percentage
      const progressPercent = Math.round((completedSections / REQUIRED_SECTIONS.length) * 100);

      console.log(`fetchAllSections for ${dashboardId}: Found ${completedSections}/${REQUIRED_SECTIONS.length} sections, allSectionsPresent=${allSectionsPresent}`);

      return {
        success: true,
        sections: processedSections,
        allSectionsPresent,
        completedSections,
        progressPercent
      };
    } catch (error) {
      console.error('Error fetching sections:', error);
      return { success: false, message: error.message };
    }
  }, [dashboardId, platformLanguage, keycloak.token, REQUIRED_SECTIONS]);

  // Stable research name = English preferred; UI label follows language toggle
  const getSubsidiaryResearchName = (subsidiary) => {
    if (typeof subsidiary === 'string') return subsidiary;
    return subsidiary?.name_en || subsidiary?.name || subsidiary?.name_jp || '';
  };

  const getSubsidiaryDisplayName = (subsidiary) => {
    if (typeof subsidiary === 'string') return subsidiary;
    if (uiLanguage === 'ja') {
      return subsidiary?.name_jp || subsidiary?.name_en || subsidiary?.name || '';
    }
    return subsidiary?.name_en || subsidiary?.name_jp || subsidiary?.name || '';
  };

  // Update the subsidiary selection handler
  const handleSubsidiarySelect = async (subsidiary) => {
    try {
      // Prefer English name for research / backend (stable across UI language toggles)
      const subsidiaryName = getSubsidiaryResearchName(subsidiary);
      setSelectedCompany(subsidiaryName);
      localStorage.setItem(`dashboard_${dashboardId}_selectedSubsidiary`, subsidiaryName);
      setShowSubsidiaryChoice(false);
      localStorage.setItem(`dashboard_${dashboardId}_showSubsidiaryChoice`, 'false');
      setIsProcessing(true);
      setProcessingStep('researchDashboard.processingSteps.initializingResearch');
      setProgressPercentage(0);
      logger.info('Subsidiary selected', { subsidiaryName });
      // Update company name in dashboard
      await authenticatedFetch(
        `api/dashboards/${dashboardId}/update-company-name`,
        { 
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ company_name: subsidiaryName })
        },
        keycloak.token
      );
      // Instead of starting research, show document upload prompt
      setPendingCompanyName(subsidiaryName);
      setShowDocumentUploadPrompt(true);
      setIsProcessing(false);
      await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.noSubsidiariesUploadPrompt'
      });
    } catch (error) {
      logger.error('Error in handleSubsidiarySelect:', error);
      setIsProcessing(false);
      await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.errorGenerating',
        i18nParams: { message: error.message }
      });
    }
  };

  // Function to handle file uploads
  const handleFileUpload = async (file) => {
    if (!file || !dashboardId) return;

    setIsFileUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Update UI with file information
      addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.fileUploaded',
        i18nParams: { filename: file.name }
      });

      // Import the authenticatedFetch function dynamically
      const { authenticatedFetch } = await import('../utils/auth');

      const response = await authenticatedFetch(
        `api/dashboards/${dashboardId}/documents`,
        {
          method: 'POST',
          body: formData,
          headers: {
            // Don't set Content-Type when using FormData
            // browsers will set it automatically with the boundary
          }
        },
        keycloak.token
      );

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.statusText}`);
      }

      const result = await response.json();
      logger.info('File uploaded successfully', result);

      // Increment file count
      setFileCount(prev => prev + 1);

      // Optionally reload documents list if that feature is implemented
      addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.fileUploaded',
        i18nParams: { filename: file.name }
      });

      // Show document sidebar if it's not already showing
      setShowDocumentSidebar(true);

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      toast.success(`Uploaded ${file.name}`);
    } catch (error) {
      logger.error('Error uploading file:', error);
      toast.error(`Failed to upload file: ${error.message}`);
    } finally {
      setIsFileUploading(false);
      setSelectedFile(null);
    }
  };


  // Function to download report as PDF with selected sections
  const downloadReportWithSections = async (selectedSections) => {
    let loadingIndicator = null;
    let timeoutId = null;

    try {
      // Show loading indicator
      loadingIndicator = document.createElement('div');
      loadingIndicator.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.7);
        color: white;
        padding: 20px;
        border-radius: 8px;
        z-index: 10000;
      `;
      loadingIndicator.id = 'pdf-loading-indicator';
      loadingIndicator.textContent = platformLanguage === 'ja' ? 'PDFを生成中...' : 'Generating PDF...';
      document.body.appendChild(loadingIndicator);

      // Safety timeout - remove the loading indicator after 60 seconds no matter what
      timeoutId = setTimeout(() => {
        removeLoadingIndicator();
      }, 60000);

      const company_name = localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`);

      // Show a message to the user
      await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.preparingPdf',
        i18nParams: { company: company_name }
      });

      // Get the authorization token
      const token = await keycloak.token;
      if (!token) {
        throw new Error('No authorization token available');
      }

      // Get the API base URL
      const apiBaseUrl = getApiBaseUrl();

      // Call the backend endpoint to generate PDF with selected sections
      const response = await fetch(
        `${apiBaseUrl}/api/dashboards/${dashboardId}/generate-pdf/${platformLanguage}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
          },
          body: JSON.stringify({
            company_name: company_name,
            selected_sections: selectedSections,
            platform_language: platformLanguage
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('PDF generation failed:', errorData);
        throw new Error(errorData.detail || 'Failed to generate PDF');
      }

      // Get the PDF blob
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${company_name}_report.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t('researchDashboard.pdfSuccess'));
    } catch (error) {
      console.error('Error in downloadReportWithSections:', error);
      toast.error(t('researchDashboard.aiMessages.pdfError'));
      throw error; // Re-throw to let the modal handle it
    } finally {
      // Clear the safety timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Always clean up the loading indicator in the finally block
      removeLoadingIndicator();
    }

    // Helper function to remove the loading indicator
    function removeLoadingIndicator() {
      try {
        // First try to find the indicator by ID
        const indicator = document.getElementById('pdf-loading-indicator');
        if (indicator) {
          document.body.removeChild(indicator);
        }
        // Fallback to the reference if ID method fails
        else if (loadingIndicator && loadingIndicator.parentNode) {
          document.body.removeChild(loadingIndicator);
        }

        // Additional check - find any elements that might be loading indicators
        const possibleIndicators = document.querySelectorAll('div[style*="z-index: 10000"]');
        possibleIndicators.forEach(el => {
          if (el.textContent.includes('PDF') || el.textContent.includes('生成中')) {
            el.parentNode.removeChild(el);
          }
        });
      } catch (cleanupError) {
        console.error('Error removing loading indicator:', cleanupError);
      }
    }
  };

  // Function to open section selection modal
  const openSectionSelectionModal = () => {
    setShowSectionSelectionModal(true);
  };

  // Function to handle language change
  const handleLanguageChange = async (newLanguage) => {
    if (isEditMode) {
      if (hasUnsavedChanges) {
        const saveAndExit = window.confirm(t('researchDashboard.editExitSaveConfirm') || 'You have unsaved changes. Do you want to save them before moving to the other report?');
        if (saveAndExit) {
          try {
            await saveSectionChanges();
            setHasUnsavedChanges(false);
          } catch (e) {
            console.error('Failed to save section changes:', e);
            return;
          }
        } else {
          const discardAndExit = window.confirm(t('researchDashboard.editExitDiscardConfirm') || 'Do you want to discard your unsaved changes and exit edit mode?');
          if (discardAndExit) {
            setHasUnsavedChanges(false);
          } else {
            return;
          }
        }
      }

      // Immediately update editedContent to cached target language's content
      if (activeSection) {
        const cachedContent = newLanguage === 'ja'
          ? (translatedSections[activeSection]?.ja || '')
          : (reportSections[activeSection] || '');
        setEditedContent(cachedContent);
      }
    }

    // **NEW**: Log localStorage values at the start of language change
    console.log('=== LANGUAGE CHANGE START ===');
    console.log('Current platformLanguage:', platformLanguage);
    console.log('New language:', newLanguage);
    console.log('Dashboard ID:', dashboardId);
    console.log('Current localStorage values:');
    console.log('  - researchInProgress:', localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`));
    console.log('  - pendingTaskId:', localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`));
    console.log('  - is_japanese_complete:', localStorage.getItem(`dashboard_${dashboardId}_is_japanese_complete`));
    console.log('  - is_english_generated:', localStorage.getItem(`dashboard_${dashboardId}_is_english_generated`));
    console.log('  - selectedCompany:', localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`));
    console.log('  - platformLanguage:', localStorage.getItem(`dashboard_${dashboardId}_platformLanguage`));
    console.log('  - initial_research_language:', localStorage.getItem(`dashboard_${dashboardId}_initial_research_language`));
    console.log('  - researchStatus:', localStorage.getItem(`dashboard_${dashboardId}_researchStatus`));
    console.log('  - isProcessComplete:', localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`));

    // Prevent language changes during active research for this specific dashboard
    const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';
    const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
    const isJapaneseComplete = localStorage.getItem(`dashboard_${dashboardId}_is_japanese_complete`) === 'true';

    // Only prevent language switching if research is in progress AND Japanese is not complete
    if (isResearchInProgress && pendingTaskId && !isJapaneseComplete) {
      console.log(`Cannot change language while research is in progress for dashboard ${dashboardId}`);
      toast.error(t('researchDashboard.cannotChangeLanguageDuringResearch'));
      return;
    }

    // **NEW**: Stop all polling before language change to prevent race conditions
    console.log(`Stopping all polling before language change for dashboard ${dashboardId}`);
    stopPolling(true);

    // If Japanese is complete and we have ongoing research, don't clear the research state
    // This allows switching between complete Japanese and in-progress English
    if (!isJapaneseComplete || !pendingTaskId) {
      // **NEW**: Clear any pending language-specific tasks and completion status
      setIsProcessing(false);
      setIsLoading(false);
      setIsProcessComplete(false);
      setResearchStatus(RESEARCH_STATUS.NOT_STARTED);

      // Clear completion status from localStorage to prevent premature completion
      localStorage.removeItem(`dashboard_${dashboardId}_researchStatus`);
      localStorage.removeItem(`dashboard_${dashboardId}_isProcessComplete`);
      localStorage.removeItem(`dashboard_${dashboardId}_isProcessing`);
      localStorage.removeItem(`dashboard_${dashboardId}_researchInProgress`);
      localStorage.removeItem(`dashboard_${dashboardId}_pendingTaskId`);

      console.log('Cleared completion status and processing flags during language switch.');
    } else {
      console.log('Preserving research state during language switch between complete Japanese and in-progress English');
    }

    // **NEW**: Clear existing sections and initialize loading states when switching languages
    // This prevents showing previous language content during the switch
    // BUT: Only clear if we're switching to a language that's currently being researched
    // If the target language already has complete data, don't clear (just fetch and update)
    if (platformLanguage !== newLanguage) {
      console.log(`Language changing from ${platformLanguage} to ${newLanguage}`);
      
      // Check if target language has existing data
      const targetLanguageComplete = newLanguage === 'ja' 
        ? localStorage.getItem(`dashboard_${dashboardId}_is_japanese_complete`) === 'true'
        : localStorage.getItem(`dashboard_${dashboardId}_is_english_generated`) === 'true';
      
      console.log(`Target language (${newLanguage}) has existing data:`, targetLanguageComplete);

      // Only clear and show loading if target language doesn't have data yet
      if (!targetLanguageComplete) {
        console.log(`Clearing existing sections and showing loading for ${newLanguage}`);
        
        // Initialize empty sections to clear previous content
        const emptyReportSections = {};
        REQUIRED_SECTIONS.forEach(section => {
          emptyReportSections[section] = '';
        });
        setReportSections(emptyReportSections);

        // Initialize section states to show loading
        const loadingSectionStates = {};
        REQUIRED_SECTIONS.forEach(section => {
          loadingSectionStates[section] = {
            content: '',
            isLoading: true,
            lastUpdated: null,
            hasData: false,
            status: 'loading',
            error: null,
            progressPercentage: 0
          };
        });
        setSectionStates(loadingSectionStates);
      } else {
        console.log(`Target language has data, will fetch and update without clearing`);
      }
    }

    // Only check if we're switching to English
    if (newLanguage === 'en') {
      // Get the Japanese completion state from localStorage or state for this dashboard
      const isJapaneseComplete = localStorage.getItem(`dashboard_${dashboardId}_is_japanese_complete`) === 'true';

      console.log('=== SWITCHING TO ENGLISH ===');
      console.log('  - isJapaneseComplete:', isJapaneseComplete);

      if (!isJapaneseComplete) {
        // Check if we have Japanese sections already for this dashboard
        const hasJapaneseSections = Object.values(reportSections).some(
          content => content && content !== '{}' && content.trim() !== ''
        );

        console.log('  - hasJapaneseSections:', hasJapaneseSections);

        // If we have Japanese sections, mark it as complete
        if (hasJapaneseSections) {
          setIsJapaneseComplete(true);
          localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
          console.log('  - Marked Japanese as complete');
        }
        // Removed the early return/block here to allow users to switch even with no data
      }
    }

    // Proceed with language change for this dashboard
    console.log(`Language changing from ${platformLanguage} to ${newLanguage} for dashboard ${dashboardId}`);
    setPlatformLanguage(newLanguage);
    localStorage.setItem(`dashboard_${dashboardId}_platformLanguage`, newLanguage);
    i18n.changeLanguage(newLanguage);

    // Reset auto-start flag when changing languages
    englishAutoStartAttempted.current = false;

    // If switching to a language with ongoing research, restart polling and initialize loading states
    const initialResearchLanguage = localStorage.getItem(`dashboard_${dashboardId}_initial_research_language`);
    if (isResearchInProgress && pendingTaskId && newLanguage === initialResearchLanguage) {
      console.log(`Switching to ${newLanguage} with ongoing research - restarting polling and initializing loading states`);

      // Initialize section states to show loading for all sections
      const initialStates = {};
      REQUIRED_SECTIONS.forEach(section => {
        initialStates[section] = {
          content: '',
          isLoading: true,
          lastUpdated: null,
          hasData: false,
          status: 'loading',
          error: null,
          progressPercentage: 0
        };
      });
      setSectionStates(initialStates);

      // Initialize reportSections with empty content
      const initialReportSections = {};
      REQUIRED_SECTIONS.forEach(section => {
        initialReportSections[section] = '';
      });
      setReportSections(initialReportSections);

      setIsProcessing(true);
      setReportGenerated(true);
      startPollingWithControls(pendingTaskId);
    }

    // **NEW**: Log localStorage values after language change
    console.log('=== LANGUAGE CHANGE COMPLETE ===');
    console.log('Updated localStorage values:');
    console.log('  - platformLanguage:', localStorage.getItem(`dashboard_${dashboardId}_platformLanguage`));
    console.log('  - is_japanese_complete:', localStorage.getItem(`dashboard_${dashboardId}_is_japanese_complete`));
    
    // **CRITICAL FIX**: After language change, fetch sections for the new language
    // This ensures content appears when switching between languages
    const companyName = localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`);
    if (companyName) {
      console.log(`Fetching sections for ${newLanguage} after language change...`);
      fetchSectionsForLanguage(companyName, newLanguage).then((fetchedData) => {
        console.log(`Successfully loaded ${newLanguage} sections after language change`);
        if (isEditMode && activeSection && fetchedData) {
          const freshContent = fetchedData[activeSection] || '';
          setEditedContent(freshContent);
          setHasUnsavedChanges(false);
        }
      }).catch((error) => {
        console.error(`Error loading ${newLanguage} sections:`, error);
      });
    }
    
    console.log('=== END LANGUAGE CHANGE ===');
  };

  const fetchSections = async () => {
    console.log('[fetchSections] Starting with dashboardId:', dashboardId);
    console.log('[fetchSections] Platform language:', platformLanguage);
    if (!dashboardId) {
      console.error('[fetchSections] dashboardId is undefined!');
      return { success: false, error: 'Dashboard ID is undefined' };
    }

    // Set flag to prevent duplicate API calls
    setIsFetchingSections(true);

    // Strictly ensure we never use Japanese endpoint when in English mode
    const endpoint = platformLanguage === 'ja'
      ? `api/dashboards/${dashboardId}/sections/japanese`
      : `api/dashboards/${dashboardId}/sections`;

    console.log('[fetchSections] Using endpoint:', endpoint);

    try {
      const response = await authenticatedFetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sections: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Fetched sections data:', Object.keys(data));

      // Process the sections to ensure they match our required format
      const processedSections = {};
      REQUIRED_SECTIONS.forEach(sectionKey => {
        if (data[sectionKey]) {
          processedSections[sectionKey] = data[sectionKey];
        } else {
          // Initialize empty sections
          processedSections[sectionKey] = '';
        }
      });

      const isEmptyData = Object.values(processedSections).every(value =>
        !value || value === "{}" || value === ""
      );
      console.log('Is data empty:', isEmptyData);

      // Count how many valid sections we have
      const validSectionsCount = Object.values(processedSections).filter(value =>
        value && value !== "{}" && value.trim() !== ""
      ).length;
      console.log(`Valid sections found: ${validSectionsCount} of ${REQUIRED_SECTIONS.length}`);

      // Mark as complete if we have all required sections
      const isComplete = validSectionsCount === REQUIRED_SECTIONS.length;

      if (isEmptyData && platformLanguage === 'en') {
        console.log('=== ENGLISH AUTO-START CHECK (FETCHSECTIONS) ===');
        console.log('Data is empty for English in fetchSections, but deferring to fetchSectionsForLanguage for auto-start logic');
        // REMOVED: Auto-start logic here to prevent duplication
        // The fetchSectionsForLanguage function will handle English auto-start with proper confirmation
        setReportGenerated(false);
        setShowStartButton(true);
      } else if (!isEmptyData) {
        console.log('Data is not empty, updating report sections');
        // Update section states to reflect loaded data
        const updatedSectionStates = {};
        Object.entries(processedSections).forEach(([key, content]) => {
          const hasContent = content && content !== '{}' && content.trim() !== '';
          updatedSectionStates[key] = {
            content: content,
            isLoading: false,
            lastUpdated: new Date().toISOString(),
            hasData: hasContent,
            status: hasContent ? 'complete' : 'pending',
            error: null,
            progressPercentage: hasContent ? 100 : 0
          };
        });

        setSectionStates(updatedSectionStates);
        if (platformLanguage === 'ja') {
          setTranslatedSections(prev => ({ ...prev, ...buildJapaneseSectionMap(processedSections) }));
          
          // CRITICAL: Also initialize reportSections with keys so tabs know sections exist
          const initializedSections = {};
          REQUIRED_SECTIONS.forEach(sectionKey => {
            initializedSections[sectionKey] = processedSections[sectionKey] ? '' : '';
          });
          setReportSections(initializedSections);
          
          ensureSectionKeys(processedSections);
        } else {
          setReportSections(normalizeReportSections(processedSections));
        }
        setReportGenerated(true);

        // Update completion flags based on language
        if (platformLanguage === 'ja') {
          setIsJapaneseComplete(isComplete);
          if (isComplete) {
            localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
          }
        } else {
          setIsEnglishComplete(isComplete);
          if (isComplete) {
            localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'true');
          }
        }

        // Ensure a section is selected
        if (shouldAutoSelectSection()) {
          const orderedKeys = getOrderedSectionKeys(processedSections);
          if (orderedKeys.length > 0) {
            applyActiveSection(orderedKeys[0]);
          }
        }
      } else if (isEmptyData && platformLanguage === 'ja') {
        console.log('Data is empty for Japanese, user will need to start research manually');
        setReportGenerated(false);
        setShowStartButton(true);
      }

      return {
        success: true,
        sections: processedSections,
        isComplete
      };
    } catch (error) {
      console.error('Error fetching sections:', error);
      // In case of error, still allow user to start research
      setShowStartButton(true);
      return {
        success: false,
        error: error.message
      };
    } finally {
      // Clear the flag after fetching completes
      setIsFetchingSections(false);
    }
  };






  // Update getSectionContent to avoid localStorage and rely on state only
  const getSectionContent = (sectionKey) => {

    if (isTranslating) {
      return t('common.loading');
    }

    const enContent = reportSections[sectionKey] || '';
    const jpContent = translatedSections[sectionKey]?.ja || '';

    if (displayLanguage === 'en') {
      return enContent || jpContent || '';
    }

    if (displayLanguage === 'jp') {
      return jpContent || enContent || '';
    }

    // both
    if (enContent && jpContent) {
      return `### English\n\n${enContent}\n\n---\n\n### 日本語\n\n${jpContent}`;
    }
    return enContent || jpContent || '';
  };






  // Add cancel research function
  // const cancelResearch = async () => {
  //   try {
  //     logger.info('Cancelling research process');

  //     // Clear all polling intervals
  //     if (statusPollingInterval) {
  //       clearInterval(statusPollingInterval);
  //       setStatusPollingInterval(null);
  //     }
  //     if (sectionsPollingInterval) {
  //       clearInterval(sectionsPollingInterval);
  //       setSectionsPollingInterval(null);
  //     }

  //     // Get the pending task ID from localStorage
  //     const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);

  //     if (pendingTaskId) {
  //       // Call the API to cancel the research task
  //       const response = await authenticatedFetch(
  //         `api/research/${dashboardId}/cancel-research-tasks/${pendingTaskId}`,
  //         {
  //           method: 'DELETE',
  //           headers: {
  //             'Content-Type': 'application/json'
  //           }
  //         },
  //         keycloak.token
  //       );

  //       if (!response.ok) {
  //         throw new Error(`Failed to cancel research task: ${response.statusText}`);
  //       }
  //     }

  //     // Reset all states
  //     setIsProcessing(false);
  //     setIsCheckingStatus(false);
  //     setProcessingStep('');
  //     setProgressPercentage(0);
  //     setProcessingTimeLeft(0);
  //     setReportGenerated(false);
  //     setShowSubsidiaryChoice(false);
  //     setSubsidiaries([]);
  //     setSelectedCompany('');
  //     setCompanyName('');
  //     setNewCompanyName('');
  //     setChatMessages([]);
  //     setResearchInProgress(false);

  //     // Clear localStorage items
  //     localStorage.removeItem(`dashboard_${dashboardId}_pendingTaskId`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_pendingCompany`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_selectedSubsidiary`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_researchInProgress`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_researchStatus`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_progressPercentage`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_isProcessing`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_activeSection`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_pollingStartTime`);
  //     localStorage.removeItem(`dashboard_${dashboardId}_lastPollingTime`);

  //     // Add cancellation message
  //     await addMessage('ai', t('researchDashboard.aiMessages.researchCancelled'));

  //     // Reset the UI state
  //     setShowStartButton(true);
  //     setDashboardDataExists(false);

  //     logger.info('Research process cancelled successfully');
  //   } catch (error) {
  //     logger.error('Error cancelling research:', error);
  //     toast.error(t('researchDashboard.errors.cancelError'));
  //   }
  // };

  // Modify the ProcessingIndicator component to show completion state and better progress messages
  const ProcessingIndicator = () => {
    // Don't show the processing indicator if the current section has content
    const currentSectionHasContent = activeSection &&
      reportSections[activeSection] &&
      reportSections[activeSection] !== '{}' &&
      reportSections[activeSection].trim() !== '';

    if (currentSectionHasContent) {
      return null;
    }

    if (!isProcessing && !isProcessComplete) return null;

    // Define thinking messages with company name replacement
    const thinkingMessages = [
      t('researchDashboard.thinkingMessages.gathering', { company: companyName }),
      t('researchDashboard.thinkingMessages.evaluating'),
      t('researchDashboard.thinkingMessages.synthesizing'),
      t('researchDashboard.thinkingMessages.analyzing'),
      t('researchDashboard.thinkingMessages.processing'),
      t('researchDashboard.thinkingMessages.reviewing'),
      t('researchDashboard.thinkingMessages.transforming'),
      t('researchDashboard.thinkingMessages.assessing'),
      t('researchDashboard.thinkingMessages.running'),
      t('researchDashboard.thinkingMessages.monitoring'),
      t('researchDashboard.thinkingMessages.validating'),
      t('researchDashboard.thinkingMessages.integrating'),
      t('researchDashboard.thinkingMessages.generating'),
      t('researchDashboard.thinkingMessages.constructing', { company: companyName }),
      t('researchDashboard.thinkingMessages.connecting')
    ];

    // State to track the current message index
    const [messageIndex, setMessageIndex] = useState(0);

    // Effect to rotate through messages
    useEffect(() => {
      // Skip if the process is complete
      if (isProcessComplete || !isProcessing) return;

      const intervalId = setInterval(() => {
        setMessageIndex(prevIndex => (prevIndex + 1) % thinkingMessages.length);
      }, 60000); // Change message every 60 seconds

      return () => clearInterval(intervalId);
    }, [isProcessing, isProcessComplete, thinkingMessages.length]);

    // For completed state, use the last message
    const completionMessage = t('researchDashboard.aiMessages.reportSuccess');

    // Current message to display
    const currentMessage = isProcessComplete
      ? completionMessage
      : thinkingMessages[messageIndex];

    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        {isProcessComplete ? (
          <>
            <div className="w-12 h-12 border-4 border-green-500 border-green-200 rounded-full mb-4"></div>
            <h3 className="text-lg font-medium mb-2 text-green-600">{t('researchDashboard.complete')}</h3>
            <p className="text-sm text-gray-600 mb-4">{completionMessage}</p>
            <div className="w-64 bg-gray-200 rounded-full h-2.5 mb-2">
              <div
                className="bg-green-500 h-2.5 rounded-full"
                style={{ width: '100%' }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mb-4">100% complete</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-t-blue-500 border-blue-200 rounded-full animate-spin mb-4"></div>
            <h3 className="text-lg font-medium mb-2">{t('researchDashboard.processingSteps.initializingResearch')}</h3>
            <p className="text-sm text-gray-600 mb-8">{currentMessage}</p>

            <button
              onClick={cancelResearch}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 flex items-center"
            >
              <X size={16} className="mr-2" />
              {researchStatus === RESEARCH_STATUS.FAILED
                ? t('researchDashboard.clearFailedResearch') || 'Clear Failed Research'
                : t('researchDashboard.cancelResearch')
              }
            </button>
          </>
        )}
      </div>
    );
  };


  // Add function to clear dashboard data with proper authorization
  const clearDashboard = async () => {
    try {
      if (!canEdit()) {
        toast.error(t('dashboard.noEditPermission'));
        return;
      }

      // Stop any active polling before clearing
      stopPolling(true);

      // Call API to clear backend data
      const response = await authenticatedFetch(
        `/api/dashboards/${dashboardId}/clear-all`,
        { method: 'DELETE' },
        keycloak.token
      );

      if (!response.ok) {
        throw new Error(`Failed to clear dashboard data: ${response.statusText}`);
      }

      // Clear all dashboard data from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(`dashboard_${dashboardId}_`)) {
          localStorage.removeItem(key);
        }
      });

      // Clear all state variables
      setDashboard(null);
      setCompany('');
      setCompanyName('');
      setNewCompanyName('');
      setSelectedCompany('');
      setSubsidiaries([]);
      setShowSubsidiaryChoice(false);
      setManualSubsidiary('');
      setViewMode('view');
      setShowDocumentSidebar(false);
      setResearchInProgress(false);
      setIsProcessing(false);
      setIsCheckingStatus(false);
      setIsLoading(false);
      setIsSearching(false);
      setReportGenerated(false);
      setShowStartButton(true);
      setDashboardDataExists(false);
      setReportSections({});
      clearActiveSectionSelection();
      localStorage.removeItem(`dashboard_${dashboardId}_activeSection`);
      setProgressPercentage(0);
      setProcessingStep('');
      setResearchStatus(RESEARCH_STATUS.NOT_STARTED);
      setChatMessages([]);
      setPromptText('');
      setIsPromptLoading(false);
      setIsTranslating(false);
      setIsInterfaceTranslating(false);
      setIsChatTranslating(false);
      setIsJapaneseComplete(false);
      setIsEnglishComplete(false);
      setIsProcessComplete(false);
      setTranslatedChatMessages({});
      setInterfaceTranslations({});
      setPollingStartTime(null);

      // Stop any active polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      setPendingTaskId(null);
      setPendingTimestamp(null);
      setSectionStates({});

      // Set default language to Japanese and update all related states
      setPlatformLanguage('ja');
      localStorage.setItem(`dashboard_${dashboardId}_platformLanguage`, 'ja');
      i18n.changeLanguage('ja');

      // Force a re-render of the language switcher
      setTimeout(() => {
        setPlatformLanguage('ja');
        i18n.changeLanguage('ja');
      }, 100);

      // Show success message
      toast.success(t('common.clearData') + ' ' + t('common.success'));

      // Reset UI state
      setShowStartButton(true);
      setDashboardDataExists(false);

      console.log('Dashboard data cleared successfully');
    } catch (error) {
      console.error('Error clearing dashboard data:', error);
      toast.error(t('common.clearData') + ' ' + t('common.error'));
    }
  };

  // Modify the SubsidiarySelector component
  const SubsidiarySelector = () => {
    if (!showSubsidiaryChoice) return null;

    const handleManualSubsidiarySubmit = async () => {
      if (!manualSubsidiary.trim() || isLoading || isProcessing) return;

      setIsLoading(true);
      try {
        // Update company name in dashboard
        const response = await authenticatedFetch(
          `/api/dashboards/${dashboardId}/update-company-name`,
          { 
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ company_name: manualSubsidiary.trim() })
          },
          keycloak.token
        );

        if (!response.ok) {
          throw new Error(`Failed to update company name: ${response.statusText}`);
        }

        // Set the selected subsidiary
        setSelectedCompany(manualSubsidiary.trim());
        localStorage.setItem(`dashboard_${dashboardId}_selectedSubsidiary`, manualSubsidiary.trim());

        // Hide subsidiary choice UI
        setShowSubsidiaryChoice(false);

        // Instead of starting research directly, show document upload prompt
        setPendingCompanyName(manualSubsidiary.trim());
        setShowDocumentUploadPrompt(true);
        setIsLoading(false);
        await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.noSubsidiariesUploadPrompt'
      });
      } catch (error) {
        logger.error('Error in handleManualSubsidiarySubmit:', error);
        setIsProcessing(false);
        setIsLoading(false);
        await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.errorGenerating',
        i18nParams: { message: error.message }
      });
      }
    };

    return (
      <div className="mt-4 mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200 subsidiary-selector">
        <div className="input-wrapper">
          <h3 className="font-medium mb-3">
            {t('researchDashboard.subsidiarySelector.title')}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('researchDashboard.subsidiarySelector.instructions')}
          </p>
        </div>

        {/* Show message when no subsidiaries are found */}
        {subsidiaries.length === 0 ? (
          <div className="p-4 mb-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-700">
            {t('researchDashboard.noSubsidiariesFound', 'Subsidiaries not present')}
          </div>
        ) : (
          <div className="subsidiary-list">
            {subsidiaries.map((subsidiary, index) => (
              <button
                key={index}
                onClick={() => handleSubsidiarySelect(subsidiary)}
                className="w-full px-4 py-2 text-left border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
              >
                {getSubsidiaryDisplayName(subsidiary)}
              </button>
            ))}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex flex-col">
            <div className="flex items-center">
              <input
                type="text"
                value={manualSubsidiary}
                onChange={(e) => setManualSubsidiary(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && manualSubsidiary.trim() && !isLoading && !isProcessing) {
                    e.preventDefault();
                    handleManualSubsidiarySubmit();
                  }
                }}
                placeholder={t('researchDashboard.enterCompany')}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading || isProcessing}
                autoFocus
              />
              <button
                onClick={handleManualSubsidiarySubmit}
                disabled={!manualSubsidiary.trim() || isProcessing || isLoading}
                className={`px-4 py-2 rounded-r-md text-white font-medium ${!manualSubsidiary.trim() || isProcessing || isLoading
                  ? 'bg-blue-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {isLoading ? t('common.loading') + "..." : t('researchDashboard.start')}
              </button>
            </div>
            {selectedCompany && (
              <p className="mt-2 text-sm text-gray-500">
                {t('researchDashboard.subsidiarySelector.currentSelection', { company: selectedCompany })}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Replace the rest of the handleAdditionalPrompt function that wasn't properly edited
  const handleAdditionalPrompt = async () => {
    if (!promptText.trim()) return;

    try {
      // Add user message to chat
      await addMessage('user', promptText);

      // Clear prompt text after sending
      const userQuestion = promptText;
      setPromptText('');

      // Set loading state
      setIsLoading(true);

      // Show typing indicator
      await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.thinking',
        isLoading: true
      });

      // Make API call to backend with the query endpoint (UI language drives answer locale)
      const response = await authenticatedFetch(
        `/api/query`,
        {
          method: 'POST',
          body: JSON.stringify({
            query: userQuestion,
            company_name: selectedCompany,
            language: (uiLanguage === 'ja' || platformLanguage === 'ja') ? 'Japanese' : 'English'
          })
        },
        keycloak.token
      );

      // Remove the temporary thinking message
      setChatMessages(prev => prev.filter(m => !m.isLoading));

      if (!response.ok) {
        throw new Error(`Failed to process query: ${response.statusText}`);
      }

      const result = await response.json();
      const data = await authenticatedFetch(`/api/dashboards/${dashboardId}/chat`, {
        method: 'POST',
        body: JSON.stringify({
          content: userQuestion + ' ' + result.response,

        })
      })
      // Add AI response to chat (store both languages for EN/JP toggle)
      if (result.response_en || result.response_jp || result.response) {
        const en = result.response_en || result.response || '';
        const jp = result.response_jp || result.response || '';
        await addMessage('ai', uiLanguage === 'ja' ? jp : en, {
          isMarkdown: true,
          content_en: en,
          content_jp: jp,
        });
      } else if (result.error) {
        throw new Error(result.error);
      } else {
        await addMessage('ai', null, {
          i18nKey: 'researchDashboard.aiMessages.noRelevantInfo'
        });
      }
    } catch (error) {
      // Remove any loading messages first
      setChatMessages(prev => prev.filter(m => !m.isLoading));
      console.error('Error processing query:', error);
      await addMessage('ai', null, {
        i18nKey: 'researchDashboard.aiMessages.error',
        i18nParams: { message: error.message }
      });
    } finally {
      setIsLoading(false);
    }
  };


  const getUniqueMessageKey = (message, index) => {
    // Use existing ID if available
    if (message.id) return message.id;

    // Use content hash + index as fallback
    const contentHash = typeof message.content === 'string'
      ? message.content.slice(0, 10).replace(/\s/g, '')
      : '';
    return `message_${index}_${contentHash}_${message.type}`;
  };

  // Add useEffect to check and start research if visiting with English for the first time


  // Add a new function to execute draft skills
  const executeDraftSkills = async () => {
    // Check if we've already executed draft skills for this dashboard
    const skillsExecutedKey = `dashboard_${dashboardId}_draft_skills_executed`;
    if (localStorage.getItem(skillsExecutedKey) === 'true') {
      logger.info('Draft skills already executed for this dashboard, skipping');
      return;
    }

    logger.info('Executing draft skills after research completion');
    try {
      const { authenticatedFetch } = await import('../utils/auth');

      // Get user email from Keycloak
      const userEmail = keycloak.tokenParsed?.email || '';

      // Send email in the request body
      const response = await authenticatedFetch(
        `/api/execute-draft-skills`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: userEmail })
        },
        keycloak.token
      );

      if (!response.ok) {
        throw new Error(`Failed to execute draft skills: ${response.statusText}`);
      }

      logger.info('Draft skills executed successfully');

      // Mark as executed in localStorage to prevent duplicate calls
      localStorage.setItem(skillsExecutedKey, 'true');
    } catch (error) {
      logger.error('Error executing draft skills:', error);
      // Silently fail - don't show error to user as this is a background process
    }
  };

  // Consolidate useEffects that depend on platformLanguage to prevent multiple calls




  const handleTranslationStart = (section) => {
    // ... existing code ...
  };

  // Add useEffect to handle document sidebar state
  useEffect(() => {
    if (showDocumentSidebar && selectedCompany) {
      // When sidebar is opened and we have a company name, trigger document fetch
      logger.info('Document sidebar opened, triggering document fetch');
    }
  }, [showDocumentSidebar, selectedCompany]);

  // Helper function to check if a section has valid content
  const hasSectionContent = (content) => {
    if (!content) return false;
    if (content === '{}') return false;
    if (typeof content === 'string' && content.trim() === '') return false;
    return true;
  };

  // Add these new state variables after the existing useState declarations
  const [sectionStates, setSectionStates] = useState({});
  const [lastPollingTime, setLastPollingTime] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Add this function after the state declarations
  const initializeSectionStates = () => {
    const initialStates = {};
    // Use the REQUIRED_SECTIONS constant for consistency
    const sectionOrder = REQUIRED_SECTIONS;

    sectionOrder.forEach(section => {
      initialStates[section] = {
        content: '',
        isLoading: true,
        lastUpdated: null,
        hasData: false,
        status: 'pending',
        error: null,
        progressPercentage: 0
      };
    });

    setSectionStates(initialStates);

    // Initialize reportSections with empty content
    const initialReportSections = {};
    sectionOrder.forEach(section => {
      initialReportSections[section] = '';
    });
    setReportSections(initialReportSections);

    // Set report as generated to show the UI
    setReportGenerated(true);

    // Auto-select the first section to show loading skeleton immediately
    if (shouldAutoSelectSection()) {
      applyActiveSection(sectionOrder[0]);
      console.log('Auto-selected first section to show loading state:', sectionOrder[0]);
    }

    console.log('Initialized section states:', initialStates);
    return sectionOrder;
  };

  // Memoize renderSectionContent to prevent expensive reprocessing on tab switches
  const renderSectionContent = useCallback((sectionKey) => {
    const content = getSectionContent(sectionKey);
    const sectionState = sectionStates[sectionKey];

    // Check if content is valid
    const hasValidContent = content &&
      content !== '{}' &&
      content.trim() !== '' &&
      content !== 'undefined';

    // Show loading state if we're processing and don't have valid content yet
    // This handles both initial research and language switches
    const shouldShowLoading = isProcessing && !hasValidContent;

    // If we have valid content, show it
    if (hasValidContent) {
      return (
        <div className="relative bg-white rounded-lg shadow p-6">
          {isEditMode && sectionKey === activeSection ? (
            // New improved editing interface
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('researchDashboard.editing')}: {getSectionTitle(sectionKey)}
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={cancelEditing}
                    disabled={isSaving}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
              <WYSIWYGEditor
                value={editedContent}
                onChange={(value) => {
                  setEditedContent(value || '');
                  setHasUnsavedChanges(true);
                }}
                onSave={saveSectionChanges}
                hasUnsavedChanges={hasUnsavedChanges}
                placeholder={t('researchDashboard.editPlaceholder') || 'Enter your content here...'}
                height={500}
              />
              {hasUnsavedChanges && (
                <div className="text-sm text-amber-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {t('researchDashboard.unsavedChanges')}
                </div>
              )}
            </div>
          ) : viewMode === 'edit' ? (
            // Old editing interface (fallback)
            <textarea
              className="w-full h-full min-h-[500px] p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={content}
              onChange={handleSectionEdit}
              placeholder={t('researchDashboard.editPlaceholder')}
            />
          ) : (
            // View mode – renderMarkdownContent already wraps in .markdown-content
            renderMarkdownContent(content, sectionKey)
          )}

        </div>
      );
    }

    // Show loading skeleton if we're processing research
    if (shouldShowLoading) {
      return (
        <div className="relative bg-white rounded-lg shadow p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
            <div className="space-y-3">
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded w-4/5"></div>
            </div>
          </div>
        </div>
      );
    }

    // Show no content message
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
        {t('researchDashboard.noContentYet')}
      </div>
    );
  }, [reportSections, translatedSections, displayLanguage, sectionStates, isProcessing, isEditMode, activeSection, isSaving, hasUnsavedChanges, editedContent, viewMode, t, renderMarkdownContent, getSectionTitle, cancelEditing, saveSectionChanges]);

  // Add a consistent tab click handler
  const handleSectionTabClick = useCallback(async (sectionKey) => {
    if (isEditMode) {
      if (hasUnsavedChanges) {
        const saveAndExit = window.confirm(t('researchDashboard.editExitSaveConfirm') || 'You have unsaved changes. Do you want to save them before moving to the other report?');
        if (saveAndExit) {
          try {
            await saveSectionChanges();
            applyActiveSection(sectionKey, { userInitiated: true });
            setIsEditMode(false);
            setHasUnsavedChanges(false);
          } catch (e) {
            console.error('Failed to save section changes:', e);
          }
        } else {
          const discardAndExit = window.confirm(t('researchDashboard.editExitDiscardConfirm') || 'Do you want to discard your unsaved changes and exit edit mode?');
          if (discardAndExit) {
            cancelEditing();
            applyActiveSection(sectionKey, { userInitiated: true });
          }
        }
      } else {
        setIsEditMode(false);
        applyActiveSection(sectionKey, { userInitiated: true });
      }
      return;
    }

    applyActiveSection(sectionKey, { userInitiated: true });
  }, [isEditMode, hasUnsavedChanges, saveSectionChanges, cancelEditing, t, applyActiveSection]);

  // Update renderSectionTabs to use the consistent handler
  const renderSectionTabs = () => {
    return (
      <div className="border-b border-gray-200 overflow-x-auto">
        <div className="flex space-x-2 px-4">
          {getOrderedSectionKeys(reportSections).map(sectionKey => {
            const sectionState = sectionStates[sectionKey];

            // Check if the section has valid content in the active language
            const hasValidContent = isSectionCompleteForLang(sectionKey, platformLanguage === 'ja' ? 'ja' : 'en');

            // Section is loading if we're processing and don't have valid content yet
            const isLoading = isProcessing && !hasValidContent;

            // Check if section was recently completed (within last 10 seconds)
            const isRecentlyCompleted = sectionState?.lastUpdated && 
              hasValidContent &&
              (new Date() - new Date(sectionState.lastUpdated)) < 10000;

            return (
              <button
                key={sectionKey}
                className={`px-4 py-3 text-sm whitespace-nowrap flex items-center relative group ${activeSection === sectionKey
                  ? 'text-blue-600 border-b-2 border-blue-600 font-medium'
                  : 'text-gray-600 hover:text-gray-900 hover:border-b-2 hover:border-gray-300'
                  } ${isRecentlyCompleted ? 'animate-pulse-subtle' : ''}`}
                onClick={() => handleSectionTabClick(sectionKey)}
              >
                <span className="flex items-center">
                  {getSectionTitle(sectionKey)}
                  <span className="ml-2 flex items-center justify-center w-3 h-3">
                    {isLoading ? (
                      <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
                    ) : hasValidContent ? (
                      <div className={`w-2 h-2 rounded-full bg-green-500 ${isRecentlyCompleted ? 'animate-ping-once' : ''}`} />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-gray-300" />
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };



  // Core polling management functions

  // Centralized function to start polling with proper safeguards
  const startPollingWithControls = (taskId, forcedLanguage = null) => {
    // **NEW**: Log localStorage values at the start of polling
    console.log('=== START POLLING WITH CONTROLS ===');
    console.log('Task ID:', taskId);
    console.log('Forced language:', forcedLanguage);
    console.log('Dashboard ID:', dashboardId);
    console.log('Current platform language:', platformLanguage);

    if (!taskId) {
      console.error(`Cannot start polling without a task ID for dashboard ${dashboardId}`);
      return;
    }

    // **NEW**: Add immediate guard against multiple polling instances
    if (pollingIntervalRef.current) {
      console.log('Polling is already active, forcing stop before starting new polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Check if research is already complete to avoid restarting polling unnecessarily
    const currentStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);
    const isComplete = localStorage.getItem(`dashboard_${dashboardId}_isProcessComplete`) === 'true';
    console.log('Start polling check - current status:', currentStatus, 'isComplete:', isComplete);

    if (currentStatus === RESEARCH_STATUS.COMPLETED || isComplete) {
      console.log('Not starting polling because research is already complete!');
      return;
    }

    // Use forced language or get initial research language for this dashboard
    const initialResearchLanguage = forcedLanguage || localStorage.getItem(`dashboard_${dashboardId}_initial_research_language`) || 'en';

    // **NEW**: More aggressive clearing of existing polling with timeout to prevent race conditions
    if (pollingIntervalRef.current) {
      console.log('Clearing existing polling interval before starting new one');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      console.log('Clearing existing polling timeout before starting new one');
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // **NEW**: Add a small delay to ensure cleanup is complete before starting new polling
    setTimeout(() => {
      // **NEW**: Double-check that we still don't have active polling before proceeding
      if (pollingIntervalRef.current) {
        console.log('Detected active polling during startup delay, aborting new polling');
        return;
      }

      console.log('=== SETTING POLLING STATES ===');

      // Set initial state
      const startTime = new Date().getTime();
      setPollingStartTime(startTime);
      setResearchStatus(RESEARCH_STATUS.IN_PROGRESS);
      setIsProcessing(true);
      setPendingTaskId(taskId); // Sync state with taskId

      // Store the current state in localStorage for this dashboard
      localStorage.setItem(`dashboard_${dashboardId}_pendingTaskId`, taskId);
      localStorage.setItem(`dashboard_${dashboardId}_pollingStartTime`, startTime);
      localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.IN_PROGRESS);
      localStorage.setItem(`dashboard_${dashboardId}_researchInProgress`, 'true');

      // **CRITICAL**: Initialize reportSections with all required keys before polling starts
      const initialReportSections = {};
      REQUIRED_SECTIONS.forEach(section => {
        initialReportSections[section] = '';
      });
      setReportSections(initialReportSections);
      
      // Initialize section states
      const initialStates = {};
      REQUIRED_SECTIONS.forEach(section => {
        initialStates[section] = {
          content: '',
          isLoading: true,
          lastUpdated: null,
          hasData: false,
          status: 'loading',
          error: null,
          progressPercentage: 0
        };
      });
      setSectionStates(initialStates);
      
      // Set reportGenerated to true immediately to show UI
      setReportGenerated(true);
      localStorage.setItem(`dashboard_${dashboardId}_reportGenerated`, 'true');

      // Create the polling function
      const pollFunction = async () => {
        try {
          console.log(`Polling both endpoints for dashboard ${dashboardId}`);

          // Fetch both English and Japanese sections in parallel to update both states in real-time
          const [enResponse, jaResponse] = await Promise.all([
            authenticatedFetch(`/api/dashboards/${dashboardId}/sections`, { method: 'GET' }).catch(err => {
              console.error("Error fetching English sections in poll:", err);
              return null;
            }),
            authenticatedFetch(`/api/dashboards/${dashboardId}/sections/japanese`, { method: 'GET' }).catch(err => {
              console.error("Error fetching Japanese sections in poll:", err);
              return null;
            })
          ]);

          let enSectionsData = {};
          let jaSectionsData = {};

          if (enResponse && enResponse.ok) {
            enSectionsData = await enResponse.json();
          }
          if (jaResponse && jaResponse.ok) {
            jaSectionsData = await jaResponse.json();
          }

          // Process sections and update UI
          const processedEnSections = {};
          const processedJaSections = {};
          let enSectionsCompleteCount = 0;
          let jaSectionsCompleteCount = 0;

          REQUIRED_SECTIONS.forEach(sectionKey => {
            const enContent = enSectionsData[sectionKey];
            const jaContent = jaSectionsData[sectionKey];

            const hasValidEn = enContent && enContent !== '{}' && enContent.trim() !== '';
            const hasValidJa = jaContent && jaContent !== '{}' && jaContent.trim() !== '';

            if (hasValidEn) {
              processedEnSections[sectionKey] = enContent;
              enSectionsCompleteCount++;
            } else {
              processedEnSections[sectionKey] = '';
            }

            if (hasValidJa) {
              processedJaSections[sectionKey] = jaContent;
              jaSectionsCompleteCount++;
            } else {
              processedJaSections[sectionKey] = '';
            }
          });

          console.log(`API data: EN=${enSectionsCompleteCount}/${REQUIRED_SECTIONS.length}, JA=${jaSectionsCompleteCount}/${REQUIRED_SECTIONS.length} sections complete`);

          // Process each section individually and update states properly
          const updatedInThisCycle = [];
          
          REQUIRED_SECTIONS.forEach(key => {
            const enContent = processedEnSections[key];
            const jaContent = processedJaSections[key];
            
            const hasValidEn = enContent && enContent !== '{}' && enContent.trim() !== '';
            const hasValidJa = jaContent && jaContent !== '{}' && jaContent.trim() !== '';

            // Update state and detect newly completed sections based on currently active platform language
            const activeLang = localStorage.getItem(`dashboard_${dashboardId}_platformLanguage`) || platformLanguage;
            const hasValidActiveContent = activeLang === 'ja' ? hasValidJa : hasValidEn;
            const activeContent = activeLang === 'ja' ? jaContent : enContent;

            // Check if this is a newly completed section for the active language
            const wasLoading = sectionStates[key]?.status === 'loading' || 
                              sectionStates[key]?.status === undefined ||
                              !sectionStates[key]?.hasData;
            const isNewlyCompleted = wasLoading && hasValidActiveContent;

            // Always update BOTH language states in the frontend cache if they have valid content
            if (hasValidEn) {
              setReportSections(prevSections => {
                if (prevSections[key] !== enContent) {
                  return { ...prevSections, [key]: enContent };
                }
                return prevSections;
              });
            }

            if (hasValidJa) {
              setTranslatedSections(prev => {
                if (prev[key]?.ja !== jaContent) {
                  return {
                    ...prev,
                    [key]: {
                      ...(prev[key] || {}),
                      ja: jaContent
                    }
                  };
                }
                return prev;
              });
              
              // Also update reportSections with empty/non-undefined value so tabs know it exists in ja mode
              setReportSections(prevSections => {
                if (prevSections[key] === undefined) {
                  return { ...prevSections, [key]: '' };
                }
                return prevSections;
              });
            }

            // Update UI sectionStates with correct loading/complete flags for active language
            if (hasValidActiveContent) {
              setSectionStates(prevStates => {
                const prev = prevStates[key] || {};
                if (prev.content !== activeContent || prev.isLoading || !prev.hasData) {
                  return {
                    ...prevStates,
                    [key]: {
                      content: activeContent,
                      isLoading: false,
                      lastUpdated: new Date().toISOString(),
                      hasData: true,
                      status: 'complete',
                      progressPercentage: 100
                    }
                  };
                }
                return prevStates;
              });



              updatedInThisCycle.push(key);
            } else {
              // Update loading state for sections without content
              setSectionStates(prevStates => {
                const prev = prevStates[key] || {};
                if (!prev.isLoading || prev.status !== 'loading') {
                  return {
                    ...prevStates,
                    [key]: {
                      ...prev,
                      isLoading: true,
                      status: 'loading',
                      progressPercentage: prev.progressPercentage || 0
                    }
                  };
                }
                return prevStates;
              });
            }
          });

          console.log(`🔄 Polling cycle complete: ${updatedInThisCycle.length} sections updated in active language:`, updatedInThisCycle.join(', '));

          // Force UI refresh
          setContentRefreshKey(prev => prev + 1);

          // Auto-select first completed section if none selected (ref-safe; skip after user click)
          if (shouldAutoSelectSection() && (enSectionsCompleteCount > 0 || jaSectionsCompleteCount > 0)) {
            const firstCompletedSection = REQUIRED_SECTIONS.find(key => {
              const activeLang = localStorage.getItem(`dashboard_${dashboardId}_platformLanguage`) || platformLanguage;
              const content = activeLang === 'ja' ? processedJaSections[key] : processedEnSections[key];
              return content && content !== '{}' && content.trim() !== '';
            });
            if (firstCompletedSection) {
              console.log(`Auto-selecting first completed section: ${firstCompletedSection}`);
              applyActiveSection(firstCompletedSection);
            }
          }

          // Count completed sections for active language progress calculation
          const activeLang = localStorage.getItem(`dashboard_${dashboardId}_platformLanguage`) || platformLanguage;
          const completedSections = activeLang === 'ja' ? jaSectionsCompleteCount : enSectionsCompleteCount;
          const progressPercent = Math.round((completedSections / REQUIRED_SECTIONS.length) * 100);
          setProgressPercentage(progressPercent);

          // Stricter decision: Research is fully complete when all 13 sections are present for BOTH English and Japanese
          const allEnComplete = enSectionsCompleteCount === REQUIRED_SECTIONS.length;
          const allJaComplete = jaSectionsCompleteCount === REQUIRED_SECTIONS.length;
          
          // Verify content length to be sure it's valid
          const enContentCheck = REQUIRED_SECTIONS.every(key => processedEnSections[key] && processedEnSections[key].length > 10);
          const jaContentCheck = REQUIRED_SECTIONS.every(key => processedJaSections[key] && processedJaSections[key].length > 10);
          
          const isActuallyComplete = allEnComplete && allJaComplete && enContentCheck && jaContentCheck;

          console.log(`Comprehensive completion check:
            allEnComplete=${allEnComplete}, 
            allJaComplete=${allJaComplete}, 
            isActuallyComplete=${isActuallyComplete}`);

          if (isActuallyComplete) {
            console.log(`All sections complete in both languages for dashboard ${dashboardId}, stopping polling`);

            // Immediately clear the polling interval to prevent any more polling cycles
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }

            // Set final progress to 100%
            setProgressPercentage(100);

            // Mark the entire dashboard as complete in localStorage BEFORE calling stopPolling
            localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.COMPLETED);
            localStorage.setItem(`dashboard_${dashboardId}_isProcessComplete`, 'true');
            localStorage.setItem(`dashboard_${dashboardId}_isProcessing`, 'false');
            localStorage.setItem(`dashboard_${dashboardId}_researchInProgress`, 'false');
            localStorage.setItem(`dashboard_${dashboardId}_progressPercentage`, '100');

            // Stop polling
            stopPolling(true);

            // Update status
            setResearchStatus(RESEARCH_STATUS.COMPLETED);
            setIsProcessing(false);
            setIsProcessComplete(true);

            // Set language-specific completion flags for this dashboard
            localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
            setIsJapaneseComplete(true);
            localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'true');
            setIsEnglishComplete(true);

            // Clear flags so English research doesn't block future tasks
            localStorage.removeItem(`dashboard_${dashboardId}_isProcessing`);
            localStorage.removeItem(`dashboard_${dashboardId}_researchInProgress`);
            localStorage.removeItem(`dashboard_${dashboardId}_pendingTaskId`);

            // Add completion message to chat only if we haven't shown it already
            const completionMessageShown = localStorage.getItem(`dashboard_${dashboardId}_completionMessageShown`) === 'true';
            const storedCompanyName = localStorage.getItem(`dashboard_${dashboardId}_selectedCompany`);
            if (!completionMessageShown && storedCompanyName) {
              addMessage('ai', null, {
                i18nKey: 'researchDashboard.aiMessages.researchCompleted',
                i18nParams: { company: storedCompanyName }
              });
              localStorage.setItem(`dashboard_${dashboardId}_completionMessageShown`, 'true');
            }
          }
        } catch (error) {
          console.error(`Error in polling for dashboard ${dashboardId}:`, error);
        }
      };

      // Start the polling interval
      pollingIntervalRef.current = setInterval(pollFunction, POLLING_INTERVAL);

      // Run immediately
      pollFunction();
    }, 100);
  };

  // Function to check if all required sections are complete
  const checkAllSectionsComplete = () => {
    // Guard against undefined reportSections
    if (!reportSections) {
      console.log('reportSections is undefined, returning false');
      return false;
    }

    // Log all sections keys in state for debugging
    console.log('All keys in reportSections:', Object.keys(reportSections));

    // Debug output content structure
    console.log('Report sections content structure (first 50 chars of each):');
    Object.entries(reportSections).forEach(([key, value]) => {
      console.log(`${key}: ${typeof value === 'string' ? value.substring(0, 50) + '...' : typeof value}`);
    });

    const incompleteSections = [];

    const allComplete = REQUIRED_SECTIONS.every(section => {
      const enContent = reportSections[section];
      const jaContent = translatedSections[section]?.ja;
      
      const hasEnContent = enContent && enContent !== '{}' && enContent.trim() !== '';
      const hasJaContent = jaContent && jaContent !== '{}' && jaContent.trim() !== '';
      
      const isValid = hasEnContent || hasJaContent;

      if (!isValid) {
        incompleteSections.push(section);
        console.log(`Section ${section} is still incomplete`);
      }

      return isValid;
    });

    const completeSectionCount = REQUIRED_SECTIONS.length - incompleteSections.length;

    if (allComplete) {
      console.log(`All ${REQUIRED_SECTIONS.length} required sections are complete!`);
    } else {
      console.log(`Found ${completeSectionCount}/${REQUIRED_SECTIONS.length} complete sections.`);
      console.log(`Missing ${incompleteSections.length} sections: ${incompleteSections.join(', ')}`);
    }

    return allComplete;
  };

  // Function to count completed sections
  const countCompletedSections = () => {
    return REQUIRED_SECTIONS.filter(section => {
      const enContent = reportSections[section];
      const jaContent = translatedSections[section]?.ja;
      
      const hasEnContent = enContent && enContent !== '{}' && enContent.trim() !== '';
      const hasJaContent = jaContent && jaContent !== '{}' && jaContent.trim() !== '';
      
      return hasEnContent || hasJaContent;
    }).length;
  };

  // Function to safely stop all polling
  const stopPolling = (force = true) => {
    console.log('**** STOPPING ALL POLLING ****');
    console.log(`Current poll state: intervalRef=${!!pollingIntervalRef.current}, timeoutRef=${!!pollingTimeoutRef.current}`);
    console.log(`Current pendingTaskId: ${localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`)}`);
    console.log(`Current research status: ${researchStatus}, stored: ${localStorage.getItem(`dashboard_${dashboardId}_researchStatus`)}`);

    const currentStatus = localStorage.getItem(`dashboard_${dashboardId}_researchStatus`);
    const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);
    const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';

    // Stop polling if:
    // 1. Force parameter is true
    // 2. Research is complete/failed
    // 3. No pending task ID
    // 4. Research is not in progress
    // 5. Research status is not_started or null
    if (force ||
      currentStatus === RESEARCH_STATUS.COMPLETED ||
      currentStatus === RESEARCH_STATUS.FAILED ||
      !pendingTaskId ||
      !isResearchInProgress ||
      currentStatus === 'not_started' ||
      !currentStatus) {
      console.log('Stopping polling - no active research or force stop:', {
        force,
        status: currentStatus,
        hasPendingTask: !!pendingTaskId,
        isInProgress: isResearchInProgress
      });

      if (pollingIntervalRef.current) {
        console.log('Clearing polling interval');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      if (pollingTimeoutRef.current) {
        console.log('Clearing polling timeout');
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      // Reset controller
      console.log('Resetting polling controller');
      setPollingController(null);

      // Update UI states but maintain skeleton and chat
      setIsProcessing(false);
      setResearchInProgress(false);

      // For COMPLETED or forced stop with all sections, properly complete the research
      if (force || currentStatus === RESEARCH_STATUS.COMPLETED) {
        const allSectionsCompleteNow = checkAllSectionsComplete();
        const completedSectionCount = countCompletedSections();

        // FIXED: Use stricter AND logic - both conditions must be true
        const isActuallyComplete = allSectionsCompleteNow && completedSectionCount === REQUIRED_SECTIONS.length;

        console.log(`stopPolling completion check: 
          allSectionsCompleteNow=${allSectionsCompleteNow}, 
          completedSectionCount=${completedSectionCount}/${REQUIRED_SECTIONS.length},
          isActuallyComplete=${isActuallyComplete}`);

        if (isActuallyComplete) {
          console.log('Research is fully complete! Finalizing research process.');
          // Set status and flags
          setResearchStatus(RESEARCH_STATUS.COMPLETED);
          setIsProcessComplete(true);
          // Update localStorage
          localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.COMPLETED);
          localStorage.setItem(`dashboard_${dashboardId}_isProcessComplete`, 'true');
        } else {
          console.log(`Research is not complete yet. Missing ${REQUIRED_SECTIONS.length - completedSectionCount} sections.`);
          // If forced but not complete, don't mark as completed
          if (force) {
            console.log('Force stopping but research is incomplete. Setting status to IN_PROGRESS.');
            setResearchStatus(RESEARCH_STATUS.IN_PROGRESS);
            localStorage.setItem(`dashboard_${dashboardId}_researchStatus`, RESEARCH_STATUS.IN_PROGRESS);
          }
        }
      }

      // Always update the processing flags in localStorage
      localStorage.setItem(`dashboard_${dashboardId}_isProcessing`, 'false');
      localStorage.setItem(`dashboard_${dashboardId}_researchInProgress`, 'false');

      console.log('Polling stopped successfully');
    } else {
      console.log(`Research is still in progress (status: ${currentStatus}), keeping polling active`);
    }
  };

  // Function to handle successful research completion
  const completeResearch = () => {
    console.log('Completing research process');
    setIsProcessing(false);
    setReportGenerated(true);

    // Enable the document button when research is completed
    enableDocumentButton();

    // Clear all polling-related state
    stopPolling();
    localStorage.removeItem(`dashboard_${dashboardId}_isProcessing`);
    localStorage.removeItem(`dashboard_${dashboardId}_researchInProgress`);
    localStorage.removeItem(`dashboard_${dashboardId}_pendingTaskId`);

    // Update completion status based on language
    if (platformLanguage === 'ja') {
      setIsJapaneseComplete(true);
      localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
    } else {
      setIsEnglishComplete(true);
      localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'true');
    }

    // Add completion message
    addMessage('ai', null, {
      i18nKey: 'researchDashboard.aiMessages.researchCompleted',
      i18nParams: { company: selectedCompany }
    });
  };



  // Enhanced clearPendingTaskData function
  // const clearPendingTaskData = () => {
  //   if (dashboardId) {
  //     // Clear all the research-related localStorage items
  //     const keysToRemove = [
  //       `dashboard_${dashboardId}_pendingTaskId`,
  //       `dashboard_${dashboardId}_pendingCompany`,
  //       `dashboard_${dashboardId}_selectedSubsidiary`,
  //       `dashboard_${dashboardId}_researchInProgress`,
  //       `dashboard_${dashboardId}_researchStatus`,
  //       `dashboard_${dashboardId}_progressPercentage`,
  //       `dashboard_${dashboardId}_isProcessing`,
  //       `dashboard_${dashboardId}_pollingStartTime`,
  //       `dashboard_${dashboardId}_lastPollingTime`
  //     ];

  //     keysToRemove.forEach(key => localStorage.removeItem(key));
  //     console.log('Cleared all pending task data from localStorage');
  //   }
  // };

  const checkExistingDashboard = async () => {
    if (!dashboardId || !keycloak.token) return;

    try {
      // Check if dashboard is already in processing mode
      const isProcessingActive = localStorage.getItem(`dashboard_${dashboardId}_isProcessing`) === 'true';
      const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';
      const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);

      // If dashboard is already in active processing mode, handle differently
      if (isProcessingActive || isResearchInProgress || pendingTaskId) {
        console.log('Dashboard is already in active processing mode, using polling logic instead');

        // Use the regular polling flow for active research
        if (pendingTaskId) {
          setIsProcessing(true);
          initializeSectionStates();
          startPollingWithControls(pendingTaskId);
          setReportGenerated(true);
        }
        return;
      }

      // Continue with normal flow for non-processing dashboards
      const dashboardResponse = await authenticatedFetch(
        `api/dashboards/${dashboardId}`,
        { method: 'GET' },
        keycloak.token
      );

      if (!dashboardResponse.ok) {
        throw new Error(`Failed to get dashboard: ${dashboardResponse.statusText}`);
      }

      const dashboardData = await dashboardResponse.json();

      // Check if dashboard has company name and research data
      if (dashboardData && dashboardData.company_name) {
        setCompanyName(dashboardData.company_name);
        setSelectedCompany(dashboardData.company_name);
        localStorage.setItem(`dashboard_${dashboardId}_selectedCompany`, dashboardData.company_name);

        // Check content based on current platform language
        const endpoint = platformLanguage === 'ja'
          ? `api/dashboards/${dashboardId}/sections/japanese`
          : `api/dashboards/${dashboardId}/sections`;

        console.log(`Checking existing content for language: ${platformLanguage}`);

        const response = await authenticatedFetch(
          endpoint,
          { method: 'GET' }
        ).catch(() => ({ ok: false }));

        if (response.ok) {
          const sections = await response.json();
          const hasContent = Object.values(sections).some(
            content => content && content !== '{}' && content.trim() !== ''
          );

          if (hasContent) {
            console.log(`Found existing content for ${platformLanguage}`);
            if (platformLanguage === 'ja') {
              setIsJapaneseComplete(true);
              localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
              setTranslatedSections(prev => ({ ...prev, ...buildJapaneseSectionMap(sections) }));
              
              // CRITICAL: Also initialize reportSections with keys so tabs know sections exist
              const initializedSections = {};
              REQUIRED_SECTIONS.forEach(sectionKey => {
                initializedSections[sectionKey] = sections[sectionKey] ? '' : '';
              });
              setReportSections(initializedSections);
              
              // **CRITICAL FIX**: Update section states to mark sections as complete
              const updatedStates = {};
              REQUIRED_SECTIONS.forEach(sectionKey => {
                const hasValidContent = sections[sectionKey] && 
                  sections[sectionKey] !== '{}' && 
                  sections[sectionKey].trim() !== '';
                
                updatedStates[sectionKey] = {
                  content: sections[sectionKey] || '',
                  isLoading: false,
                  lastUpdated: hasValidContent ? new Date().toISOString() : null,
                  hasData: hasValidContent,
                  status: hasValidContent ? 'complete' : 'pending',
                  error: null,
                  progressPercentage: hasValidContent ? 100 : 0
                };
              });
              setSectionStates(updatedStates);
              console.log(`Updated section states for ${Object.keys(updatedStates).length} Japanese sections`);
              
              ensureSectionKeys(sections);
            } else {
              setIsEnglishComplete(true);
              localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'true');
              setReportSections(normalizeReportSections(sections));
              
              // **CRITICAL FIX**: Update section states for English sections too
              const updatedStates = {};
              REQUIRED_SECTIONS.forEach(sectionKey => {
                const hasValidContent = sections[sectionKey] && 
                  sections[sectionKey] !== '{}' && 
                  sections[sectionKey].trim() !== '';
                
                updatedStates[sectionKey] = {
                  content: sections[sectionKey] || '',
                  isLoading: false,
                  lastUpdated: hasValidContent ? new Date().toISOString() : null,
                  hasData: hasValidContent,
                  status: hasValidContent ? 'complete' : 'pending',
                  error: null,
                  progressPercentage: hasValidContent ? 100 : 0
                };
              });
              setSectionStates(updatedStates);
              console.log(`Updated section states for ${Object.keys(updatedStates).length} English sections`);
            }
            setReportGenerated(true);
            setIsProcessing(false);

            // Enable the document button when existing content is loaded
            enableDocumentButton();

            // Select first section only if no section is currently active
            const orderedKeys = getOrderedSectionKeys(sections);
            if (orderedKeys.length > 0 && shouldAutoSelectSection()) {
              applyActiveSection(orderedKeys[0]);
            }
          } else {
            console.log(`No content found for ${platformLanguage}`);
            setShowStartButton(true);
          }
        } else {
          console.log(`Failed to fetch content for ${platformLanguage}`);
          setShowStartButton(true);
        }
      } else {
        // No company name, show start button
        setShowStartButton(true);
      }
    } catch (error) {
      console.error('Error checking existing dashboard:', error);
      setShowStartButton(true);
    }
  };

  // Helper function to fetch sections based on language
  const fetchSectionsForLanguage = async (companyName, lang = platformLanguage) => {
    const isProcessingActive = localStorage.getItem(`dashboard_${dashboardId}_isProcessing`) === 'true';
    const isResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';
    const pendingTaskId = localStorage.getItem(`dashboard_${dashboardId}_pendingTaskId`);

    // Determine which endpoint to use based on language
    const endpoint = lang === 'ja'
      ? `api/dashboards/${dashboardId}/sections/japanese`
      : `api/dashboards/${dashboardId}/sections`;

    console.log('[fetchSectionsForLanguage] Using endpoint:', endpoint);

    try {
      const sectionsResponse = await authenticatedFetch(
        endpoint,
        { method: 'GET' },
        keycloak.token
      );

      if (sectionsResponse.ok) {
        const sectionsData = await sectionsResponse.json();

        // Check if we got valid sections
        const hasValidSections = Object.values(sectionsData).some(
          content => content && content !== '{}' && content.trim() !== ''
        );

        if (hasValidSections) {
          // Process and display sections
          if (lang === 'ja') {
            setTranslatedSections(prev => ({ ...prev, ...buildJapaneseSectionMap(sectionsData) }));
            
            // CRITICAL: Also initialize reportSections with keys so tabs know sections exist
            const initializedSections = {};
            REQUIRED_SECTIONS.forEach(sectionKey => {
              initializedSections[sectionKey] = sectionsData[sectionKey] ? '' : '';
            });
            setReportSections(initializedSections);
            
            // **CRITICAL FIX**: Update section states to mark sections as complete
            const updatedStates = {};
            REQUIRED_SECTIONS.forEach(sectionKey => {
              const hasValidContent = sectionsData[sectionKey] && 
                sectionsData[sectionKey] !== '{}' && 
                sectionsData[sectionKey].trim() !== '';
              
              updatedStates[sectionKey] = {
                content: sectionsData[sectionKey] || '',
                isLoading: false,
                lastUpdated: hasValidContent ? new Date().toISOString() : null,
                hasData: hasValidContent,
                status: hasValidContent ? 'complete' : 'pending',
                error: null,
                progressPercentage: hasValidContent ? 100 : 0
              };
            });
            setSectionStates(updatedStates);
            console.log(`[fetchSectionsForLanguage] Updated section states for ${Object.keys(updatedStates).length} Japanese sections`);
            
            ensureSectionKeys(sectionsData);
          } else {
            setReportSections(normalizeReportSections(sectionsData));
            
            // **CRITICAL FIX**: Update section states for English sections
            const updatedStates = {};
            REQUIRED_SECTIONS.forEach(sectionKey => {
              const hasValidContent = sectionsData[sectionKey] && 
                sectionsData[sectionKey] !== '{}' && 
                sectionsData[sectionKey].trim() !== '';
              
              updatedStates[sectionKey] = {
                content: sectionsData[sectionKey] || '',
                isLoading: false,
                lastUpdated: hasValidContent ? new Date().toISOString() : null,
                hasData: hasValidContent,
                status: hasValidContent ? 'complete' : 'pending',
                error: null,
                progressPercentage: hasValidContent ? 100 : 0
              };
            });
            setSectionStates(updatedStates);
            console.log(`[fetchSectionsForLanguage] Updated section states for ${Object.keys(updatedStates).length} English sections`);
          }
          setReportGenerated(true);
          setIsProcessing(false);

          // Enable the document button when content is loaded
          enableDocumentButton();

          // Set completion flags based on language
          if (lang === 'ja') {
            setIsJapaneseComplete(true);
            localStorage.setItem(`dashboard_${dashboardId}_is_japanese_complete`, 'true');
            // Don't set English completion flag for Japanese content
            setIsEnglishComplete(false);
            localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'false');
          } else if (lang === 'en') {
            setIsEnglishComplete(true);
            localStorage.setItem(`dashboard_${dashboardId}_is_english_generated`, 'true');
          }

          // Select first section only if no section is currently active
          const orderedKeys = getOrderedSectionKeys(sectionsData);
          if (orderedKeys.length > 0 && shouldAutoSelectSection()) {
            applyActiveSection(orderedKeys[0]);
          }
          return sectionsData;
        } else {
          // No valid sections found for this language - removed isJapaneseComplete requirement
          if (platformLanguage === 'en'
            && !isProcessingActive && !isResearchInProgress && !pendingTaskId
            && !englishAutoStartAttempted.current) {
            // Only show confirmation and potentially start research if not already processing
            // and if we haven't already attempted auto-start

            console.log('=== SHOWING ENGLISH RESEARCH CONFIRMATION ===');
            englishAutoStartAttempted.current = true; // Mark that we've attempted auto-start

            // Stay in English mode and just show the start button if no data exists
            console.log('No English data found, showing start button');
            setShowStartButton(true);
            englishAutoStartAttempted.current = true;
          } else {
            // Otherwise show start button
            setShowStartButton(true);
          }
        }
      } else {
        // API error, show start button
        setShowStartButton(true);
      }
    } catch (error) {
      console.error('Error fetching sections for language:', error);
      setShowStartButton(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Call this in useEffect
  useEffect(() => {
    checkExistingDashboard();
  }, [dashboardId, keycloak.token]);

  // Add another useEffect to refetch when language changes
  useEffect(() => {
    // **NEW**: Add debouncing and safety checks to prevent multiple rapid calls
    if (!isInitialLoad && selectedCompany && !isProcessing && !isLoading) {
      // **NEW**: Add a small delay to allow state changes to settle
      const timeoutId = setTimeout(() => {
        // **NEW**: Double-check conditions before making API call
        const currentIsProcessing = localStorage.getItem(`dashboard_${dashboardId}_isProcessing`) === 'true';
        const currentIsResearchInProgress = localStorage.getItem(`dashboard_${dashboardId}_researchInProgress`) === 'true';

        if (!currentIsProcessing && !currentIsResearchInProgress) {
          console.log(`Language change effect: fetching sections for ${platformLanguage}`);
          fetchSectionsForLanguage(selectedCompany);
        } else {
          console.log(`Language change effect: skipping fetch due to active processing`);
        }
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [platformLanguage, selectedCompany, isInitialLoad, isProcessing, isLoading]);

  // Fetch team permissions when dashboard is loaded
  useEffect(() => {
    if (dashboard && dashboard.team_id) {
      // Refresh permissions to ensure we have the latest access levels
      refreshPermissions();
      console.log('Refreshed team permissions for dashboard:', dashboard.id);
    }
  }, [dashboard, refreshPermissions]);

  const [isDocumentButtonEnabled, setIsDocumentButtonEnabled] = useState(() => {
    // Read from localStorage, default to false
    const dashboardId = window.location.pathname.split('/').pop();
    return localStorage.getItem(`dashboard_${dashboardId}_documentsEnabled`) === 'true';
  });

  // Helper to enable document button and persist in localStorage
  const enableDocumentButton = useCallback(() => {
    const dashboardId = window.location.pathname.split('/').pop();
    console.log('[enableDocumentButton] Enabling document button for dashboard:', dashboardId);
    setIsDocumentButtonEnabled(true);
    localStorage.setItem(`dashboard_${dashboardId}_documentsEnabled`, 'true');
  }, []);

  // Helper to disable document button and persist in localStorage
  const disableDocumentButton = useCallback(() => {
    const dashboardId = window.location.pathname.split('/').pop();
    console.log('[disableDocumentButton] Disabling document button for dashboard:', dashboardId);
    setIsDocumentButtonEnabled(false);
    localStorage.setItem(`dashboard_${dashboardId}_documentsEnabled`, 'false');
  }, []);

  // On dashboard load, always disable document button until flow is complete
  useEffect(() => {
    disableDocumentButton();
  }, [dashboardId]);

  // Add new state for document upload prompt and flow
  const [showDocumentUploadPrompt, setShowDocumentUploadPrompt] = useState(false);
  const [showDocumentUploadUI, setShowDocumentUploadUI] = useState(false);
  const [pendingCompanyName, setPendingCompanyName] = useState('');
  const [highlightDocumentsButton, setHighlightDocumentsButton] = useState(false);

  return (
    <div className="research-dashboard flex h-screen overflow-hidden">
      <Toaster position="top-right" />

      {/* Initial loading overlay - shows for 5 seconds when dashboard opens */}

      {/* Left panel - Chat */}
      <div className="w-1/3 border-r border-gray-200 flex flex-col bg-white">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/dashboards')}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 mr-3"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-medium text-gray-800">{t('researchDashboard.researchAssistant')}</h1>
              <div className="text-xs text-gray-500">{chatMessages.length} {t('researchDashboard.messages')}</div>
            </div>
          </div>

          {/* UI Language toggle — switches all interface text */}
          <div className="flex items-center gap-1 p-0.5 bg-slate-100 rounded-lg border border-gray-200">
            {[{ id: 'en', label: 'EN' }, { id: 'ja', label: 'JP' }].map(({ id, label }) => (
              <button
                key={id}
                onClick={() => handleUiLanguageChange(id)}
                title={id === 'en' ? 'Switch interface to English' : 'インターフェースを日本語に切替'}
                className={`px-3 py-1 rounded-md text-xs font-bold transition-all duration-200 ${
                  uiLanguage === id
                    ? 'bg-white text-blue-600 shadow-sm ring-1 ring-black/5'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Add floating translation progress indicator */}
        {isGlobalTranslating && (
          <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg p-4 z-50 min-w-[300px]">
            <div className="flex items-center mb-2">
              <Loader size={16} className="animate-spin mr-2" />
              <span className="text-sm font-medium text-gray-700">
                {t('common.translating')}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${translationProgress}%` }}
              />
            </div>
            <div className="text-xs text-gray-500 mt-1 text-right">
              {translationProgress}%
            </div>
          </div>
        )}

        {/* Add translation in progress overlay for sections */}
        {Object.keys(sectionsTranslating).length > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
              <div className="flex items-center justify-center mb-4">
                <Loader size={24} className="animate-spin mr-3" />
                <span className="text-lg font-medium text-gray-800">
                  {t('common.translating')}
                </span>
              </div>
              <p className="text-sm text-gray-600 text-center">
                {t('researchDashboard.translatingContent')}
              </p>
            </div>
          </div>
        )}

        {/* Chat section with processing indicator */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4">
          {chatMessages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-6">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Database className="text-gray-400" size={28} />
              </div>
              <h2 className="text-lg font-medium text-gray-700 mb-2">{t('researchDashboard.researchAssistant')}</h2>
              <p className="text-gray-500 text-sm">
                {t('researchDashboard.enterCompanyToGenerate')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {chatMessages.map((message, index) => {
                // Support both 'type' field (frontend) and 'is_user' field (backend)
                const isUserMessage = message.type === 'user' || message.is_user === true;
                return (
                  <div
                    key={getUniqueMessageKey(message, index)}
                    className={`w-full flex ${isUserMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`w-fit max-w-[85%] rounded-lg p-3 text-left ${isUserMessage
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-800'
                        }`}
                    >
                      {renderChatMessage(message)}
                    </div>
                  </div>
                );
              })}

              {/* Display subsidiary selector in the chat flow */}
              {showSubsidiaryChoice && <div className="w-full"><SubsidiarySelector /></div>}

              {/* Document upload prompt (Yes/No) */}
              {showDocumentUploadPrompt && (
                <div className="flex space-x-2 mt-4">
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    onClick={async () => {
                      // User chose Yes: show upload UI
                      setShowDocumentUploadPrompt(false);
                      enableDocumentButton();
                      setHighlightDocumentsButton(true);
                      await authenticatedFetch(
                        `api/dashboards/${dashboardId}/update-company-name`,
                        { 
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ company_name: pendingCompanyName })
                        },
                        keycloak.token
                      );
                      setSelectedCompany(pendingCompanyName); // <-- ensure DocumentManager gets the correct company name
                      setCompanyName(pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_selectedCompany`, pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_companyName`, pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_initial_research_language`, platformLanguage);
                      // await addMessage('ai', t('researchDashboard.aiMessages.uploadDocumentsNowInstruction'));
                      setShowDocumentUploadUI(true); // Now this just shows the Done button and message
                    }}
                  >
                    {t('common.yes')}
                  </button>
                  <button
                    className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400"
                    onClick={async () => {
                      // User chose No: enable document button and start research
                      setShowDocumentUploadPrompt(false);
                      enableDocumentButton();
                      setHighlightDocumentsButton(false);
                      await authenticatedFetch(
                        `api/dashboards/${dashboardId}/update-company-name`,
                        { 
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ company_name: pendingCompanyName })
                        },
                        keycloak.token
                      );
                      setSelectedCompany(pendingCompanyName); // <-- ensure DocumentManager gets the correct company name
                      setCompanyName(pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_selectedCompany`, pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_companyName`, pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_initial_research_language`, platformLanguage);
                      await startResearchProcess(pendingCompanyName);
                    }}
                  >
                    {t('common.no')}
                  </button>
                </div>
              )}

              {/* Document upload UI and Done button */}
              {showDocumentUploadUI && (
                <div className="flex flex-col space-y-2 mt-4 items-start">
                  <div className="bg-blue-50 border border-blue-300 text-blue-800 rounded px-4 py-3 flex items-center">
                    <span role="img" aria-label="document" className="mr-2">📄</span>
                    {t('researchDashboard.aiMessages.uploadDocumentsNowInstruction')}
                  </div>
                  <button
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 mt-2"
                    onClick={async () => {
                      setShowDocumentUploadUI(false);
                      setHighlightDocumentsButton(false);
                      setSelectedCompany(pendingCompanyName); // <-- ensure DocumentManager gets the correct company name
                      setCompanyName(pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_selectedCompany`, pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_companyName`, pendingCompanyName);
                      localStorage.setItem(`dashboard_${dashboardId}_initial_research_language`, platformLanguage);
                      await startResearchProcess(pendingCompanyName);
                    }}
                  >
                    {t('common.done')}
                  </button>
                </div>
              )}

              {/* Show processing indicator only after research has actually started (not during subsidiary/upload steps) */}
              {(isProcessing || researchStatus === RESEARCH_STATUS.IN_PROGRESS) && researchInProgress && (
                <div className="w-full flex justify-start">
                  <div className="w-fit max-w-[85%] rounded-lg p-3 text-left bg-gray-100 text-gray-800">
                    <div className="flex items-center">
                      <Loader2 className="animate-spin mr-2 flex-shrink-0" size={16} />
                      <span>
                        {processingStep?.startsWith('researchDashboard.')
                          ? t(processingStep)
                          : (processingStep === 'Initializing research...' ||
                             processingStep === 'Initializing research process...' ||
                             processingStep === '調査プロセスを初期化しています...')
                            ? t('researchDashboard.processingSteps.initializingResearch')
                            : (processingStep || t('researchDashboard.processing'))}
                      </span>
                    </div>
                    {progressPercentage > 0 && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {Math.round(progressPercentage)}% {t('researchDashboard.complete')}
                        </div>
                      </div>
                    )}

                    {/* Always show cancel button during research */}
                    <div className="mt-3">
                      <button
                        onClick={() => {
                          if (window.confirm(t('researchDashboard.cancelConfirm'))) {
                            cancelResearch();
                          }
                        }}
                        className="flex items-center text-sm text-red-600 hover:text-red-700 transition-colors"
                      >
                        <X size={14} className="mr-1" />
                        {researchStatus === RESEARCH_STATUS.FAILED
                          ? t('researchDashboard.clearFailedResearch') || 'Clear Failed Research'
                          : t('researchDashboard.cancelResearch')
                        }
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200">
          {/* Only show input field when not showing subsidiary selector and not processing */}
          {!showSubsidiaryChoice && !isProcessing && !reportGenerated && (
            <div className="space-y-2">

              <div className="flex items-center">
                <input
                  type="text"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCompanyName && !isLoading && !isProcessing) {
                      e.preventDefault();
                      const startButton = e.target.nextElementSibling;
                      if (startButton) {
                        startButton.click();
                      }
                    }
                  }}
                  placeholder={t('researchDashboard.enterCompany')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoading || isProcessing}
                />
                <button
                  data-start-research
                  onClick={async () => {

                    if (!newCompanyName) {
                      toast.error(t('researchDashboard.errors.companyRequired'));
                      return;
                    }

                    setIsLoading(true);

                    try {
                      const { authenticatedFetch } = await import('../utils/auth');
                      // Subsidiary names must be a single language — "Both" confuses Gemini
                      // and used to cause empty/failed lookups that silently fell through
                      // to the upload-docs prompt.
                      const subsidiaryLanguage = uiLanguage === 'en' ? 'English' : 'Japanese';
                      const companyResponse = await authenticatedFetch(
                        'api/company',
                        {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            company_name: newCompanyName,
                            language: subsidiaryLanguage
                          })
                        },
                        keycloak.token
                      );

                      if (!companyResponse.ok) {
                        throw new Error(`Failed to check for subsidiaries: ${companyResponse.statusText}`);
                      }

                      const companyData = await companyResponse.json();

                      // Gemini/API failure must NOT be treated as "no subsidiaries"
                      if (companyData.lookup_failed) {
                        const raw = companyData.message || '';
                        const isQuota =
                          /quota|429|rate limit/i.test(raw);
                        const friendly = isQuota
                          ? (t('researchDashboard.errors.geminiQuotaExceeded', {
                              defaultValue:
                                'Gemini API quota exceeded. Wait about a minute and try again, or add another API key.',
                            }))
                          : (raw || t('researchDashboard.errors.checkingSubsidiaries'));
                        toast.error(friendly);
                        await addMessage('ai', null, {
                          i18nKey: isQuota
                            ? 'researchDashboard.errors.geminiQuotaExceeded'
                            : 'researchDashboard.aiMessages.errorGenerating',
                          i18nParams: isQuota
                            ? undefined
                            : { message: friendly }
                        });
                        setIsLoading(false);
                        return;
                      }

                      if (companyData.has_subsidiaries && companyData.subsidiaries && companyData.subsidiaries.length > 0) {
                        setSubsidiaries(companyData.subsidiaries);
                        localStorage.setItem(
                          `dashboard_${dashboardId}_subsidiaries`,
                          JSON.stringify(companyData.subsidiaries)
                        );
                        setShowSubsidiaryChoice(true);
                        localStorage.setItem(`dashboard_${dashboardId}_showSubsidiaryChoice`, 'true');
                        await addMessage('ai', null, {
                          i18nKey: 'researchDashboard.aiMessages.hasSubsidiaries',
                          i18nParams: {
                            company: newCompanyName,
                            count: companyData.subsidiaries.length
                          }
                        });
                        await addMessage('ai', null, {
                          i18nKey: 'researchDashboard.aiMessages.selectSubsidiary'
                        });

                        setIsLoading(false);
                      } else {
                        // Successful lookup with no child companies — upload docs prompt
                        setPendingCompanyName(newCompanyName);
                        setShowDocumentUploadPrompt(true);
                        setIsLoading(false);
                        await addMessage('ai', null, {
                          i18nKey: 'researchDashboard.aiMessages.noSubsidiariesUploadPrompt'
                        });
                      }
                    } catch (error) {
                      logger.error('Error checking for subsidiaries:', error);
                      toast.error(error.message || t('researchDashboard.errors.checkingSubsidiaries'));
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading || isProcessing || !newCompanyName}
                  className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {isLoading ? t('common.loading') + "..." : t('researchDashboard.start')}
                </button>
              </div>

              {/* Add optional fields */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={stockTicker}
                  onChange={(e) => setStockTicker(e.target.value)}
                  placeholder={t('researchDashboard.stockTickerOptional')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isProcessing}
                />
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder={t('researchDashboard.industryOptional')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isProcessing}
                />
              </div>
            </div>
          )}

          {/* Show chat input for additional prompts when report is generated */}
          {reportGenerated && !isProcessing && !showSubsidiaryChoice && (
            <div className="flex items-center">
              <input
                type="text"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && promptText.trim() && !isLoading) {
                    e.preventDefault();
                    handleAdditionalPrompt();
                  }
                }}
                placeholder={t('researchDashboard.enterPrompt')}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
              <button
                onClick={handleAdditionalPrompt}
                disabled={isLoading || !promptText}
                className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700 disabled:bg-blue-300"
              >
                {isLoading ? "..." : t('researchDashboard.ask')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right panel - Report */}
      <div className="w-2/3 flex flex-col bg-gray-50 overflow-hidden">

        {/* Action Toolbar */}
        <div className="p-4 bg-white border-b border-gray-200 flex justify-between items-center shrink-0">
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center">
              <Building size={20} className="mr-2 text-blue-500" />
              {reportGenerated ? `${selectedCompany} ${t('researchDashboard.report')}` : t('researchDashboard.title')}
            </h1>
            {reportGenerated && (
              <div className="text-xs text-slate-400 mt-0.5 flex items-center">
                <Clock size={12} className="mr-1" />
                {Object.keys(reportSections).filter(sectionKey => isSectionCompleteForLang(sectionKey, platformLanguage === 'ja' ? 'ja' : 'en')).length} / {Object.keys(reportSections).length} {t('researchDashboard.sectionsAvailable')}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {/* Document Manager */}
            <button
              className={`flex items-center px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all duration-200 ${highlightDocumentsButton ? 'ring-4 ring-blue-300/50 animate-pulse' : ''}`}
              onClick={() => setShowDocumentSidebar(!showDocumentSidebar)}
              disabled={!isDocumentButtonEnabled}
            >
              <FileText size={16} className="mr-2" />
              {t('common.documents')}
            </button>

            {/* Edit Mode */}
            {reportGenerated && activeSection && (
              <button
                className={`flex items-center px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold transition-all duration-200 ${isEditMode ? 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100' : 'text-slate-600 hover:bg-slate-50'}`}
                onClick={isEditMode ? cancelEditing : startEditingSection}
                disabled={isSaving}
              >
                {isEditMode ? (
                  <><X size={16} className="mr-2" />{t('common.cancel')}</>
                ) : (
                  <><Edit size={16} className="mr-2" />{t('common.edit')}</>
                )}
              </button>
            )}

            {/* Clear Data (Owner/Write only) */}
            {dashboard && (isOwner(dashboard) || hasWriteAccess(dashboard)) && (
              <button
                className="flex items-center px-4 py-2 border border-red-100 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-all duration-200"
                onClick={() => {
                  if (!isProcessing && window.confirm(t('common.clearDataConfirm'))) {
                    clearDashboard();
                  }
                }}
                disabled={isProcessing}
              >
                <Trash2 size={16} className="mr-2" />
                {t('common.clearData')}
              </button>
            )}

            {/* Generate PDF/Report */}
            {reportGenerated && (
              <button
                className="flex items-center px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:shadow-lg hover:shadow-blue-900/20 transition-all duration-300"
                onClick={openSectionSelectionModal}
              >
                <Download size={16} className="mr-2" />
                {t('researchDashboard.generateReport')}
              </button>
            )}
          </div>
        </div>

          {/* Section tabs */}
          {reportGenerated && renderSectionTabs()}

          {/* Main content area */}
          <div className="flex-1 overflow-y-auto">
            {reportGenerated && activeSection ? (
              <div className="p-6">
                <div key={contentRefreshKey} className="markdown-wrapper">
                  {renderSectionContent(activeSection)}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-6">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FileText className="text-gray-400" size={28} />
                </div>
                <h2 className="text-xl font-medium text-gray-700 mb-2">{t('researchDashboard.noReport')}</h2>
                <p className="text-gray-500 text-sm mb-8 text-center max-w-md">
                  {t('researchDashboard.enterCompanyToGenerate')}
                </p>
              </div>
            )}
          </div>

          {/* Status bar at the bottom */}
          {reportGenerated && (
            <div className="border-t border-gray-200 px-4 py-2 bg-gray-50 text-xs text-gray-500 flex justify-between">
              <div>
                {t('researchDashboard.currentSection')}: <span className="font-medium">{getSectionTitle(activeSection)}</span>
              </div>

              <div>
                <span className="inline-flex items-center">
                  <span className="w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                  {isProcessing ? t('researchDashboard.updating') : t('researchDashboard.autoSaved')}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Document sidebar (displayed conditionally) */}
        {showDocumentSidebar && (
          <div className="fixed right-0 top-0 h-full w-[450px] bg-white shadow-lg z-30 sidebar-panel">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-medium">{t('researchDashboard.documents')}</h3>
              <button
                onClick={() => setShowDocumentSidebar(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 p-4 overflow-y-auto">
              <DocumentManager
                key={`doc-manager-${showDocumentSidebar}-${selectedCompany}`}
                companyName={selectedCompany}
                dashboardId={dashboardId}
              />
            </div>
          </div>
        )}

        {/* Section Selection Modal */}
        <SectionSelectionModal
          isOpen={showSectionSelectionModal}
          onClose={() => setShowSectionSelectionModal(false)}
          dashboardId={dashboardId}
          platformLanguage={platformLanguage}
          companyName={selectedCompany}
          onDownload={downloadReportWithSections}
        />

      </div>
      );
}

      export default ResearchDashboard;

